/**
 * Layout Overlay Map Hook — F-01C: simultaneous multi-layout rendering
 *
 * Renders ALL vector PDF layouts simultaneously, each with its own
 * Mapbox source/layer set keyed by the layout id:
 *   Image     — `layout-image-<id>` (raster)
 *   Labels    — `layout-labels-<id>` (symbol)
 *   Module    — `layout-module-grid-<id>` (fill / line / symbol)
 *
 * Visibility rules:
 *   - Global legend toggles (`layerVisibility.layoutImage` /
 *     `layerVisibility.layoutLabels`) are the master ON/OFF for all layouts.
 *   - Per-layout `imageVisible` / `labelVisible` flags veto rendering for
 *     that specific layout even when the global toggle is on.
 *
 * Click routing:
 *   - Each per-layout layer registers its own Mapbox event handlers.
 *   - The handler closure captures the owning `layoutId`.
 *   - On mousedown, we first call `setActiveLayoutId(layoutId)` so the
 *     deprecated `data.siteLayout` alias (kept in lockstep by F-01B's
 *     active-layout helper) swaps to the clicked layout.
 *   - The existing drag/edit logic then reads `data.siteLayout` and
 *     operates on the correct layout without per-handler refactoring.
 *
 * Density filter:
 *   - When TOTAL textEntities across all visible layouts exceeds 5000,
 *     module-level labels are dropped per-layout. The threshold is global,
 *     not per-layout (one layout with 4000 entities + another with 4000
 *     entities = 8000 total → dense).
 *
 * Diff loop:
 *   - `prevLayoutsRef` tracks (transform, pageImage url, grid config) per
 *     sanitized layout id. On each sync we (1) remove sources/layers for
 *     layouts no longer present, (2) add/update sources/layers for present
 *     layouts, (3) keep handler registrations idempotent.
 *
 * Legacy fallback:
 *   - When `data.siteLayouts` is undefined/empty, falls back to
 *     `data.siteLayout` (treated as a length-1 array) so unmigrated
 *     projects keep rendering.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useMapStore } from '../../stores/mapStore';
import { useUIStore } from '../../stores/uiStore';
import { entitiesToGeoJSON, pageImageCorners, inverseTransformPoint } from '../../domain/layout/transform';
import { generateModuleGridGeoJSON, stringGridFrame } from '../../domain/topology/moduleGrid';
import { metersToDeg } from '../../utils/geometry';
import { resolveSignedUrl } from '../../services/signedAssetUrl';
import { buildLabelTextSizeExpr, buildLabelSortKeyExpr, labelSizeAt, MODULE_DETAIL_MINZOOM, STRING_LINE_MINZOOM } from '../../utils/labelZoom';
import { deriveStringLines } from '../../utils/stringLines';
import { numericFeatureId } from '../../utils/featureIds';
import type { SiteLayout, LayoutTransform, StringOverride } from '../../domain/layout/types';
import type { Map as MapboxMap, GeoJSONSource, MapMouseEvent } from 'mapbox-gl';

/** Drag threshold in pixels — below this it's a click, above it's a drag */
const DRAG_THRESHOLD = 4;

/** Total-entity threshold (across all visible layouts) for dropping module-level labels */
const DENSITY_THRESHOLD = 5000;

const LEVEL_COLORS: Record<string, string> = {
  string: '#ffffff',
  inverter: '#3b82f6',
  mppt: '#06b6d4',
  dccb: '#f97316',
  station: '#ef4444',
  module: '#a855f7',
  row: '#ec4899',
  zone: '#14b8a6',
  unclassified: '#fbbf24',
};

// ─── Per-layout Mapbox ID helpers ──────────────────────────────────────────
//
// Mapbox source/layer IDs must be deterministic and free of characters that
// can confuse style-layer lookups. We sanitize the raw layout id (which is
// typically `layout-<unix-ms>` from LayoutImportModal but could be anything
// after F-01B's collision-safe id derivation) to lowercase alphanumeric +
// hyphen + underscore. The sanitized id is used ONLY for Mapbox IDs; the
// user-visible layout name is never used here. Exported for unit tests.
export function sanitizeLayoutId(id: string): string {
  return id.replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
}
export function layoutImageSourceId(id: string): string {
  return `layout-image-${sanitizeLayoutId(id)}`;
}
export function layoutImageLayerId(id: string): string {
  return `layout-image-layer-${sanitizeLayoutId(id)}`;
}
export function layoutLabelsSourceId(id: string): string {
  return `layout-labels-${sanitizeLayoutId(id)}`;
}
export function layoutLabelsLayerId(id: string): string {
  return `layout-labels-layer-${sanitizeLayoutId(id)}`;
}
export function layoutGridSourceId(id: string): string {
  return `layout-module-grid-${sanitizeLayoutId(id)}`;
}
export function layoutGridFillLayerId(id: string): string {
  return `layout-module-grid-fill-${sanitizeLayoutId(id)}`;
}
export function layoutGridOutlineLayerId(id: string): string {
  return `layout-module-grid-outline-${sanitizeLayoutId(id)}`;
}
export function layoutGridLabelsLayerId(id: string): string {
  return `layout-module-grid-labels-${sanitizeLayoutId(id)}`;
}
export function layoutStringLinesSourceId(id: string): string {
  return `layout-string-lines-${sanitizeLayoutId(id)}`;
}
export function layoutStringLinesLayerId(id: string): string {
  return `layout-string-lines-layer-${sanitizeLayoutId(id)}`;
}
export function layoutStringPolarityLayerId(id: string): string {
  return `layout-string-polarity-${sanitizeLayoutId(id)}`;
}

/**
 * Density filter — decide whether module-level labels should be dropped
 * given the total entity count across all visible layouts. Exported so
 * unit tests can lock the contract.
 */
export function shouldApplyDensityFilter(totalEntities: number): boolean {
  return totalEntities > DENSITY_THRESHOLD;
}

/**
 * Did any STRING-level entity change between two textEntities arrays?
 *
 * The store updates immutably and `updateTextEntity`/`batchUpdateTextEntities`
 * map over the array preserving order and reusing untouched entity objects, so
 * per-index reference comparison identifies exactly the changed entities. Any
 * length change (add/delete/reorder-by-rebuild) counts as changed wholesale.
 * Exported for unit tests.
 */
export function stringLevelEntitiesChanged(
  prev: SiteLayout['textEntities'] | undefined,
  next: SiteLayout['textEntities'],
): boolean {
  if (prev === next) return false;
  if (!prev || prev.length !== next.length) return true;
  for (let i = 0; i < next.length; i++) {
    if (prev[i] !== next[i]) {
      if (
        prev[i]?.classification?.level === 'string' ||
        next[i]?.classification?.level === 'string'
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Build Mapbox match expression for level colors, merging standard + custom */
function buildLevelColorExpr(customColors?: Record<string, string>) {
  const expr: (string | string[])[] = ['match', ['get', 'level']];
  const merged = { ...LEVEL_COLORS, ...customColors };
  for (const [level, color] of Object.entries(merged)) {
    expr.push(level, color);
  }
  expr.push('#9ca3af'); // default fallback
  return expr as unknown;
}

/**
 * Convert entities to a GeoJSON FeatureCollection and inject `layoutId` into
 * every feature's properties. The injected `layoutId` is what click handlers
 * and rect-selection use to identify the owning layout.
 */
function entitiesToGeoJSONForLayout(
  entities: SiteLayout['textEntities'],
  transform: LayoutTransform,
  layoutId: string,
): GeoJSON.FeatureCollection {
  const gj = entitiesToGeoJSON(entities, transform);
  for (const f of gj.features) {
    if (!f.properties) f.properties = {};
    f.properties.layoutId = layoutId;
    // TOP-LEVEL id, not just promoteId: dynamic geojson sources DROP any
    // feature whose feature.id is null (GeoJSONRT.load: `if (id == null)
    // continue`) — with properties-only ids the whole layer renders empty.
    // updateData diffs key on this same id.
    // NUMERIC id (hash of entityId): the RT tiler drops features at scale
    // when ids are STRINGS — proven on the 30k-feature grid (holes; A/B'd
    // 2026-08-26). Selection/feature-state still key on the string property
    // via promoteId; updateData keys on this numeric id, which clones carry.
    f.id = numericFeatureId(f.properties.entityId);
  }
  return gj;
}

/**
 * Wrapper for generateModuleGridGeoJSON that injects `layoutId` into every
 * feature's properties (same routing concern as labels).
 */
function generateModuleGridForLayout(layout: SiteLayout): GeoJSON.FeatureCollection {
  const gj = generateModuleGridGeoJSON(layout);
  for (const f of gj.features) {
    if (!f.properties) f.properties = {};
    f.properties.layoutId = layout.id;
    // Stable per-module id for promoteId, so selection can be painted with
    // setFeatureState (O(selection)) instead of regenerating + re-sending the
    // whole grid (O(strings x modules), measured seconds per click).
    // moduleNumber alone is NOT unique — it repeats per string.
    f.properties.fsid = `${f.properties.entityId}:${f.properties.moduleNumber}`;
    // TOP-LEVEL id too: dynamic geojson sources drop id-less features
    // outright (see entitiesToGeoJSONForLayout), and updateData keys on it.
    // NUMERIC (hashed) — string ids made the RT tiler drop whole/partial
    // strings at grid scale (the owner-reported holes, A/B-proven).
    f.id = numericFeatureId(f.properties.fsid);
  }
  return gj;
}

// ─── Diff-loop bookkeeping types ────────────────────────────────────────────

interface PrevLayoutSnapshot {
  rawId: string;
  transform: LayoutTransform;
  pageImageUrl: string | undefined;
  /**
   * Change-detection refs for the EXPENSIVE half of a sync (measured: labels
   * geojson rebuild + setData = 33-138 ms of main-thread stringify at 2k-8k
   * labels; module grid regen + setData = 0.8-1.6 s at 26k-52k polygons —
   * per layout, on every sync, previously unconditional).
   *
   * The store updates immutably (applyActiveLayoutUpdate replaces only the
   * touched layout object and only the arrays it changed), so reference
   * equality on the specific inputs each product reads is an exact "did the
   * data change" test:
   *   labels geojson reads  textEntities + transform (+ the global density flag)
   *   grid geojson reads    textEntities + transform + moduleGridConfig + stringOverrides
   * Paint/filter/visibility setters are cheap and stay unconditional — only
   * the geojson rebuild + setData is gated on these.
   */
  textEntities: SiteLayout['textEntities'];
  moduleGridConfig: SiteLayout['moduleGridConfig'];
  stringOverrides: SiteLayout['stringOverrides'];
  densityFiltered: boolean;
}

interface LayoutHandlerSet {
  rawLayoutId: string;
  labelsLayerId: string;
  gridFillLayerId: string;
  onLabelDown: (e: MapMouseEvent) => void;
  onLabelEnter: () => void;
  onLabelLeave: () => void;
  onModuleDown: (e: MapMouseEvent) => void;
  onModuleEnter: () => void;
  onModuleLeave: () => void;
}

export default function useLayoutOverlay(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
  /** false = render the surface but register NO label/grid mouse handlers
   *  (share viewers, R3-D: the layout is view-only for them; the drag and
   *  select machinery below must never arm). */
  interactive: boolean = true,
) {
  // Per-sanitized-id state for diff
  const prevLayoutsRef = useRef<Map<string, PrevLayoutSnapshot>>(new Map());
  // Per-sanitized-id handler registrations (so we can unregister cleanly)
  const handlersRef = useRef<Map<string, LayoutHandlerSet>>(new Map());

  useEffect(() => {
    if (!isMapReady) return;

    // Last geojson BUILT for each layout's sources, by sanitized id. Lets a
    // dragstart split off the moving features WITHOUT regenerating the whole
    // collection (grid generation alone is ~1s+ at 26k+ polygons on a weak
    // machine — that regen was the "drag takes time to react" the owner felt).
    // Always current with what is on screen: written on every build, and the
    // memoized fast paths only run when the data is unchanged.
    const labelsGeojsonCache = new Map<string, GeoJSON.FeatureCollection>();
    const gridGeojsonCache = new Map<string, GeoJSON.FeatureCollection>();

    // Features currently hidden for a drag, by source. Hiding uses
    // feature-state-driven OPACITY, not filters and never data rewrites:
    // setFilter triggers an async worker re-bucket of every cached tile
    // (verified in mapbox-gl 3.18 — Style.setFilter → updateSourceCache
    // 'reload'), which left the movers visibly duplicated for the re-bucket
    // window; feature-state is evaluated at paint time, so hide and unhide
    // are genuinely one-frame. The hidden state also SURVIVES setData, which
    // is exactly right: the dragend rebuild arrives with the movers still
    // hidden, and the teardown unhides them in the same frame it removes the
    // scratch copies — a ghost-free swap in both directions.
    /** Plain paint opacity. (This once multiplied in a feature-state
     *  drag-hide gate; updateData moves features in place instead of hiding
     *  them, and plain numbers keep every slider tick a cheap constant
     *  update rather than a data-driven re-evaluation.) */
    function opacityWithHidden(base: number) {
      return base;
    }

    /**
     * Resolve the effective layouts list — prefer `data.siteLayouts` (the
     * F-01A canonical shape); fall back to the legacy `data.siteLayout`
     * singleton (treated as a length-1 array) for unmigrated projects.
     * Filters to layouts that have a `transform` (the others aren't
     * registered against map space yet).
     */
    function getEffectiveLayouts(): SiteLayout[] {
      const data = useProjectStore.getState().data;
      const arr = data.siteLayouts && data.siteLayouts.length > 0
        ? data.siteLayouts
        : (data.siteLayout ? [data.siteLayout] : []);
      return arr.filter((l) => !!l && !!l.transform);
    }

    function getActiveLayout(): SiteLayout | undefined {
      return useProjectStore.getState().data.siteLayout;
    }

    function removeLayerSetForLayout(map: MapboxMap, sanitizedId: string) {
      try {
        const ids = [
          layoutStringPolarityLayerId(sanitizedId),
          layoutStringLinesLayerId(sanitizedId),
          layoutGridLabelsLayerId(sanitizedId),
          layoutGridOutlineLayerId(sanitizedId),
          layoutGridFillLayerId(sanitizedId),
          layoutLabelsLayerId(sanitizedId),
          layoutImageLayerId(sanitizedId),
        ];
        for (const id of ids) {
          if (map.getLayer(id)) map.removeLayer(id);
        }
        const srcIds = [
          layoutStringLinesSourceId(sanitizedId),
          layoutGridSourceId(sanitizedId),
          layoutLabelsSourceId(sanitizedId),
          layoutImageSourceId(sanitizedId),
        ];
        for (const id of srcIds) {
          if (map.getSource(id)) map.removeSource(id);
        }
      } catch { /* ignore — style may already be torn down */ }
    }

    function renderImageForLayout(
      map: MapboxMap,
      layout: SiteLayout,
      sanitizedId: string,
      transformChanged: boolean,
      globalImageOn: boolean,
    ) {
      const sourceId = layoutImageSourceId(sanitizedId);
      const layerId = layoutImageLayerId(sanitizedId);
      if (!layout.pageImage || !layout.transform) {
        // No image data — clean up if a layer exists
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        return;
      }
      const needsCreate = transformChanged || !map.getSource(sourceId);
      if (needsCreate) {
        const desiredUrl = layout.pageImage.url;
        const desiredId = layout.id;
        (async () => {
          let signedUrl = desiredUrl;
          try { signedUrl = await resolveSignedUrl(desiredUrl); } catch { /* fall back */ }
          // State may have moved on during the await — re-read by id and bail
          // if this layout was removed or its image swapped.
          const layouts = getEffectiveLayouts();
          const cur = layouts.find((l) => l.id === desiredId);
          if (!cur?.pageImage || !cur.transform || cur.pageImage.url !== desiredUrl) return;
          const corners = pageImageCorners(
            cur.pageImage.pageWidthPt,
            cur.pageImage.pageHeightPt,
            cur.transform,
          );
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
          map.addSource(sourceId, { type: 'image', url: signedUrl, coordinates: corners });
          map.addLayer({
            id: layerId,
            type: 'raster',
            source: sourceId,
            paint: { 'raster-opacity': cur.imageOpacity },
          });
          const curGlobalImageOn = useMapStore.getState().layerVisibility.layoutImage;
          map.setLayoutProperty(
            layerId,
            'visibility',
            curGlobalImageOn && cur.imageVisible ? 'visible' : 'none',
          );
        })();
      } else if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'raster-opacity', layout.imageOpacity);
      }
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(
          layerId,
          'visibility',
          globalImageOn && layout.imageVisible ? 'visible' : 'none',
        );
      }
    }

    function renderLabelsForLayout(
      map: MapboxMap,
      layout: SiteLayout,
      sanitizedId: string,
      transformChanged: boolean,
      labelsDataChanged: boolean,
      densityFiltered: boolean,
      globalLabelsOn: boolean,
      hiddenLevels: string[],
    ) {
      const sourceId = layoutLabelsSourceId(sanitizedId);
      const layerId = layoutLabelsLayerId(sanitizedId);
      if (!layout.transform) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        return;
      }
      const needsCreate = transformChanged || !map.getSource(sourceId);
      // The rebuild + setData is the expensive half (main-thread stringify of
      // the whole collection, then a full worker re-tile + glyph re-shape).
      // Skip it when the inputs it reads are unchanged — an opacity tick or a
      // legend toggle must not pay for 5,000 labels.
      if (!needsCreate && !labelsDataChanged) {
        if (map.getLayer(layerId)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setPaintProperty(layerId, 'text-opacity', opacityWithHidden(layout.labelOpacity) as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setPaintProperty(layerId, 'text-color', buildLevelColorExpr(layout.customLevelColors) as any);
        }
        applyLabelFilterAndVisibility(map, layerId, layout, globalLabelsOn, hiddenLevels);
        return;
      }
      const entitiesToRender = densityFiltered
        ? layout.textEntities.filter((e) => e.classification?.level !== 'module')
        : layout.textEntities;
      const geojson = entitiesToGeoJSONForLayout(entitiesToRender, layout.transform, layout.id);
      labelsGeojsonCache.set(sanitizedId, geojson);
      if (needsCreate) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        // promoteId gives every feature a stable id (feature-state +
        // updateData both key on it); dynamic enables updateData — the
        // in-place per-frame drag mechanism.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map.addSource(sourceId, { type: 'geojson', data: geojson, promoteId: 'entityId', dynamic: true } as any);
        map.addLayer({
          id: layerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['get', 'text'],
            // Sharp-labels WP-B: zoom ladder instead of a fixed 11px — a
            // fixed screen size made a 13MWp site a label CARPET at site
            // zoom. Size follows the ground per level (macro levels first,
            // modules only near); collision culls overflow with macro
            // priority. The drag ghost mirrors labelSizeAt() — change the
            // table in utils/labelZoom.ts, never the numbers here.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'text-size': buildLabelTextSizeExpr() as any,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-padding': 1,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'symbol-sort-key': buildLabelSortKeyExpr() as any,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-anchor': 'center',
          },
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'text-color': buildLevelColorExpr(layout.customLevelColors) as any,
            'text-halo-color': 'rgba(0,0,0,0.7)',
            'text-halo-width': 1,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'text-opacity': opacityWithHidden(layout.labelOpacity) as any,
          },
        });
      } else {
        const src = map.getSource(sourceId);
        if (src && 'setData' in src) {
          (src as GeoJSONSource).setData(geojson);
        }
        if (map.getLayer(layerId)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setPaintProperty(layerId, 'text-opacity', opacityWithHidden(layout.labelOpacity) as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setPaintProperty(layerId, 'text-color', buildLevelColorExpr(layout.customLevelColors) as any);
        }
      }
      applyLabelFilterAndVisibility(map, layerId, layout, globalLabelsOn, hiddenLevels);
    }

    /** The cheap tail of the labels pass — shared by the fast (memoized) and
     *  full paths so the two cannot drift. */
    function applyLabelFilterAndVisibility(
      map: MapboxMap,
      layerId: string,
      layout: SiteLayout,
      globalLabelsOn: boolean,
      hiddenLevels: string[],
    ) {
      if (map.getLayer(layerId)) {
        // Only touch the filter when it actually changes: mapbox compares the
        // stored `undefined` against our `null` as UNEQUAL, so an
        // unconditional setFilter(null) reloads the whole source (full worker
        // re-bucket) on EVERY sync for layers with no hidden levels.
        const desired = hiddenLevels.length > 0
          ? ['!', ['in', ['get', 'level'], ['literal', hiddenLevels]]]
          : null;
        const current = map.getFilter(layerId) ?? null;
        if (JSON.stringify(current) !== JSON.stringify(desired)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setFilter(layerId, desired as any);
        }
        map.setLayoutProperty(
          layerId,
          'visibility',
          globalLabelsOn && layout.labelVisible ? 'visible' : 'none',
        );
      }
    }

    function renderGridForLayout(
      map: MapboxMap,
      layout: SiteLayout,
      sanitizedId: string,
      transformChanged: boolean,
      gridDataChanged: boolean,
      globalLabelsOn: boolean,
      moduleLevelHidden: boolean,
      stringLinesOn: boolean,
      zoomOverride: boolean,
    ) {
      const sourceId = layoutGridSourceId(sanitizedId);
      const fillId = layoutGridFillLayerId(sanitizedId);
      const outlineId = layoutGridOutlineLayerId(sanitizedId);
      const labelsId = layoutGridLabelsLayerId(sanitizedId);
      const lineSourceId = layoutStringLinesSourceId(sanitizedId);
      const lineLayerId = layoutStringLinesLayerId(sanitizedId);
      const polarityId = layoutStringPolarityLayerId(sanitizedId);
      if (!layout.transform || !layout.moduleGridConfig) {
        if (map.getLayer(polarityId)) map.removeLayer(polarityId);
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
        if (map.getSource(lineSourceId)) map.removeSource(lineSourceId);
        if (map.getLayer(labelsId)) map.removeLayer(labelsId);
        if (map.getLayer(outlineId)) map.removeLayer(outlineId);
        if (map.getLayer(fillId)) map.removeLayer(fillId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        return;
      }
      const gridConfig = layout.moduleGridConfig;
      // layout.labelVisible is the PER-LAYOUT master (VectorLayoutTicket
      // Display checkbox): it vetoes this layout's grid and lines too, so a
      // single layout can be inspected in isolation on multi-layout sites.
      const layoutShown = layout.labelVisible !== false;
      const gridVisible = gridConfig.gridVisible && globalLabelsOn && !moduleLevelHidden && layoutShown;
      // Lines are their OWN legend toggle, independent of the grid's (owner
      // design 2026-08-26: grid and lines show the same string twice — the
      // user chooses grid only, lines only, or both).
      const linesVisible = stringLinesOn && gridConfig.gridVisible && globalLabelsOn && layoutShown;
      // Zoom ranges: ladder by default; the all-zooms override serves the
      // whole-site assessment pass while positioning a layout. Default is a
      // HANDOFF — lines carry the string story at mid zoom and bow out when
      // the grid takes over (unless the grid is toggled off).
      const applyZoomRanges = () => {
        const gridMin = zoomOverride ? 0 : MODULE_DETAIL_MINZOOM;
        for (const id of [fillId, outlineId, labelsId]) {
          if (map.getLayer(id)) map.setLayerZoomRange(id, gridMin, 24);
        }
        const lineMin = zoomOverride ? 0 : STRING_LINE_MINZOOM;
        const lineMax = zoomOverride || !gridVisible ? 24 : MODULE_DETAIL_MINZOOM;
        for (const id of [lineLayerId, polarityId]) {
          if (map.getLayer(id)) map.setLayerZoomRange(id, lineMin, lineMax);
        }
      };
      const needsCreate = transformChanged || !map.getSource(sourceId);
      // This is the single most expensive statement in the layout editor —
      // strings x modules polygons regenerated and re-serialized (measured
      // 0.8-1.6 s at 26k-52k features, per layout, per sync). Only pay it
      // when an input the grid actually reads has changed. Side benefit: a
      // sync that skips setData no longer wipes a selection highlight that
      // applySelectionHighlight painted onto the live source data.
      if (!needsCreate && !gridDataChanged) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(fillId)) map.setPaintProperty(fillId, 'fill-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(outlineId)) map.setPaintProperty(outlineId, 'line-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(labelsId)) map.setPaintProperty(labelsId, 'text-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(lineLayerId)) map.setPaintProperty(lineLayerId, 'line-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        const memoGridVisibility = gridVisible ? 'visible' : 'none';
        if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', memoGridVisibility);
        if (map.getLayer(outlineId)) map.setLayoutProperty(outlineId, 'visibility', memoGridVisibility);
        if (map.getLayer(labelsId)) map.setLayoutProperty(labelsId, 'visibility', memoGridVisibility);
        const memoLinesVisibility = linesVisible ? 'visible' : 'none';
        if (map.getLayer(lineLayerId)) map.setLayoutProperty(lineLayerId, 'visibility', memoLinesVisibility);
        if (map.getLayer(polarityId)) map.setLayoutProperty(polarityId, 'visibility', memoLinesVisibility);
        applyZoomRanges();
        return;
      }
      const gridGeojson = generateModuleGridForLayout(layout);
      gridGeojsonCache.set(sanitizedId, gridGeojson);
      // WP-C: string lines derive FROM the grid (polarity order 1→N through
      // module centers), so they regenerate exactly when the grid does and a
      // moved module bends its line automatically. The label-text map lets
      // colors group by the naming hierarchy's parent (inverter areas share
      // a hue).
      const labelTextById = new Map<string, string>();
      for (const ent of layout.textEntities ?? []) {
        if (ent.id && ent.text) labelTextById.set(ent.id, ent.text);
      }
      const stringLines = deriveStringLines(gridGeojson, labelTextById);
      if (needsCreate) {
        if (map.getLayer(labelsId)) map.removeLayer(labelsId);
        if (map.getLayer(outlineId)) map.removeLayer(outlineId);
        if (map.getLayer(fillId)) map.removeLayer(fillId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        // promoteId: selection feature-state and per-frame updateData both
        // key on fsid; dynamic enables updateData. Feature-state survives
        // setData (keyed by id), so a data refresh does not wipe highlights.
        // dynamic: FALSE — deliberately. The RT tiler is unreliable at this
        // feature count (~30k): it dropped whole/partial strings with string
        // ids, and still dropped tiles at specific zooms with numeric ids
        // (owner A/B'd both, 2026-08-26). The classic tiler renders
        // everything; drag commits go through one setData from the patched
        // cache instead of updateData (a one-time ~1s settle at dragend on
        // huge grids — correctness beats it). promoteId stays for
        // feature-state selection.
        map.addSource(sourceId, { type: 'geojson', data: gridGeojson, promoteId: 'fsid' });
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: sourceId,
          // Module-scale detail arrives as ONE thing: rectangles, outlines
          // and numbers share MODULE_DETAIL_MINZOOM (owner: the rectangle
          // carpet at mid zoom, numberless, was noise).
          minzoom: MODULE_DETAIL_MINZOOM,
          paint: {
            'fill-color': [
              'interpolate', ['linear'],
              ['/', ['get', 'moduleNumber'], ['get', 'totalModules']],
              0, 'rgba(34, 197, 94, 0.25)',
              0.5, 'rgba(234, 179, 8, 0.25)',
              1, 'rgba(239, 68, 68, 0.25)',
            ],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'fill-opacity': opacityWithHidden(gridConfig.gridOpacity) as any,
          },
        });
        map.addLayer({
          id: outlineId,
          type: 'line',
          source: sourceId,
          minzoom: MODULE_DETAIL_MINZOOM,
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'line-color': ['case', ['boolean', ['feature-state', 'selected'], false], '#13d6c6', 'rgba(255, 255, 255, 0.5)'] as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2.5, 0.5] as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'line-opacity': opacityWithHidden(gridConfig.gridOpacity) as any,
          },
        });
        map.addLayer({
          id: labelsId,
          type: 'symbol',
          source: sourceId,
          minzoom: MODULE_DETAIL_MINZOOM,
          layout: {
            'text-field': ['to-string', ['get', 'moduleNumber']],
            'text-size': 9,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.8)',
            'text-halo-width': 1,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'text-opacity': opacityWithHidden(gridConfig.gridOpacity) as any,
          },
        });
        // WP-C: derived string lines — polarity-order polylines with
        // direction chevrons and +/− endpoints. Same visibility discipline
        // as the labels: they arrive WITH the string labels, polarity signs
        // with module detail.
        if (map.getLayer(polarityId)) map.removeLayer(polarityId);
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
        if (map.getSource(lineSourceId)) map.removeSource(lineSourceId);
        // Same reliability rule as the grid source: classic tiler, no dynamic.
        map.addSource(lineSourceId, { type: 'geojson', data: stringLines });
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: lineSourceId,
          // Lines arrive WITH the module grid, not before it (owner smoke:
          // lines without grid context read as spaghetti at mid zoom).
          minzoom: MODULE_DETAIL_MINZOOM,
          filter: ['==', ['get', 'kind'], 'line'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'lineColor'],
            'line-width': ['interpolate', ['linear'], ['zoom'], MODULE_DETAIL_MINZOOM, 1, 21.5, 2.5],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'line-opacity': opacityWithHidden(gridConfig.gridOpacity) as any,
          },
        });
        // Polarity endpoints — owner design: a bold + at module 1 and − at
        // module N, visible whenever the LINE is visible (chevrons were
        // dropped: too subtle to read direction from).
        map.addLayer({
          id: polarityId,
          type: 'symbol',
          source: lineSourceId,
          minzoom: STRING_LINE_MINZOOM,
          filter: ['==', ['get', 'kind'], 'polarity'],
          layout: {
            'text-field': ['get', 'sign'],
            'text-size': 14,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          },
          paint: {
            'text-color': ['get', 'lineColor'],
            'text-halo-color': 'rgba(0,0,0,0.9)',
            'text-halo-width': 1.5,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'text-opacity': opacityWithHidden(gridConfig.gridOpacity) as any,
          },
        });
      } else {
        const src = map.getSource(sourceId);
        if (src && 'setData' in src) {
          (src as GeoJSONSource).setData(gridGeojson);
        }
        const lsrc = map.getSource(lineSourceId);
        if (lsrc && 'setData' in lsrc) {
          (lsrc as GeoJSONSource).setData(stringLines);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(fillId)) map.setPaintProperty(fillId, 'fill-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(outlineId)) map.setPaintProperty(outlineId, 'line-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(labelsId)) map.setPaintProperty(labelsId, 'text-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (map.getLayer(lineLayerId)) map.setPaintProperty(lineLayerId, 'line-opacity', opacityWithHidden(gridConfig.gridOpacity) as any);
      }
      const gridVisibility = gridVisible ? 'visible' : 'none';
      if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', gridVisibility);
      if (map.getLayer(outlineId)) map.setLayoutProperty(outlineId, 'visibility', gridVisibility);
      if (map.getLayer(labelsId)) map.setLayoutProperty(labelsId, 'visibility', gridVisibility);
      const linesVisibility = linesVisible ? 'visible' : 'none';
      if (map.getLayer(lineLayerId)) map.setLayoutProperty(lineLayerId, 'visibility', linesVisibility);
      if (map.getLayer(polarityId)) map.setLayoutProperty(polarityId, 'visibility', linesVisibility);
      applyZoomRanges();
    }

    function syncLayouts() {
      const map = mapRef.current;
      if (!map) return;
      const layouts = getEffectiveLayouts();
      const { layerVisibility: vis, layoutLabelLevels } = useMapStore.getState();

      // Global density: total entities across all eligible layouts.
      const totalEntities = layouts.reduce((s, l) => s + (l.textEntities?.length ?? 0), 0);
      const dense = shouldApplyDensityFilter(totalEntities);

      // Hidden levels (per-level legend sub-toggles). Custom levels default
      // visible unless explicitly toggled off. Applied per-layout.
      const hiddenLevels = Object.entries(layoutLabelLevels)
        .filter(([, v]) => !v)
        .map(([k]) => k);
      const moduleLevelHidden = layoutLabelLevels.module === false;

      // Diff: remove layouts no longer present
      const currentIdsSanitized = new Set(layouts.map((l) => sanitizeLayoutId(l.id)));
      for (const prevSanitized of [...prevLayoutsRef.current.keys()]) {
        if (!currentIdsSanitized.has(prevSanitized)) {
          removeLayerSetForLayout(map, prevSanitized);
          unregisterHandlersForLayout(map, prevSanitized);
          prevLayoutsRef.current.delete(prevSanitized);
          labelsGeojsonCache.delete(prevSanitized);
          gridGeojsonCache.delete(prevSanitized);
        }
      }

      // Per-layout work bundle — compute change flags once per layout so we
      // can reuse them across the three render passes below. The data-changed
      // flags are exact under the store's immutable-update discipline: a
      // commit replaces only the touched layout's arrays, so untouched
      // layouts (and untouched halves of the touched layout) keep their
      // references and skip their rebuild entirely. This is what removes the
      // multi-second post-mouseup freeze: previously EVERY layout's labels
      // AND grid were regenerated and re-sent on every store change.
      const work = layouts.map((layout) => {
        const sanitized = sanitizeLayoutId(layout.id);
        const prev = prevLayoutsRef.current.get(sanitized);
        const transformChanged = !prev || prev.transform !== layout.transform;
        return {
          layout,
          sanitized,
          transformChanged,
          labelsDataChanged:
            transformChanged ||
            !prev ||
            prev.textEntities !== layout.textEntities ||
            prev.densityFiltered !== dense,
          gridDataChanged:
            transformChanged ||
            !prev ||
            // The grid reads only STRING-level entities (cx/cy/rotation), so a
            // moved title or row label must not trigger a multi-second grid
            // regen. The scan is O(n) reference compares — microseconds.
            stringLevelEntitiesChanged(prev.textEntities, layout.textEntities) ||
            prev.moduleGridConfig !== layout.moduleGridConfig ||
            prev.stringOverrides !== layout.stringOverrides,
        };
      });

      // Z-order contract:
      //   bottom →    all layout images       (raster, opaque pixel data)
      //              ↓
      //                all module grids       (semi-transparent fill + outline)
      //              ↓
      //   top →     all layout labels         (text — must be readable above everything)
      //
      // Mapbox `addLayer` with no beforeId appends to the END of the layer
      // stack (= top of z-order). Iterating all layouts in three separate
      // passes guarantees the final stack order is images → grids → labels
      // regardless of how many layouts are present. The pre-F-01C singleton
      // code achieved this implicitly because its `syncLayout` had three
      // sequential blocks; per-layout iteration broke that until this fix.

      // Pass 1: images (bottom)
      for (const { layout, sanitized, transformChanged } of work) {
        renderImageForLayout(map, layout, sanitized, transformChanged, vis.layoutImage);
      }
      // Pass 2: module grids (middle)
      for (const { layout, sanitized, transformChanged, gridDataChanged } of work) {
        renderGridForLayout(map, layout, sanitized, transformChanged, gridDataChanged, vis.layoutLabels, moduleLevelHidden,
          vis.stringLines !== false, useMapStore.getState().layoutZoomOverride);
      }
      // Pass 3: labels (top — always above grid + image so text stays readable)
      for (const { layout, sanitized, transformChanged, labelsDataChanged } of work) {
        renderLabelsForLayout(map, layout, sanitized, transformChanged, labelsDataChanged, dense, vis.layoutLabels, hiddenLevels);
      }

      // Pass 4: handler registration + bookkeeping (no layer side-effects)
      for (const { layout, sanitized } of work) {
        if (!handlersRef.current.has(sanitized)) {
          registerHandlersForLayout(map, layout.id, sanitized);
        }
        prevLayoutsRef.current.set(sanitized, {
          rawId: layout.id,
          transform: layout.transform!,
          pageImageUrl: layout.pageImage?.url,
          textEntities: layout.textEntities,
          moduleGridConfig: layout.moduleGridConfig,
          stringOverrides: layout.stringOverrides,
          densityFiltered: dense,
        });
      }

      // Selection is feature-state, which dies with a source RECREATE (though
      // it survives setData). Reapplying here is O(selection) — cheap — and
      // fixes the long-standing "any rebuild silently wipes the highlight".
      // Late-bound because the initial sync runs before the module-selection
      // state exists (TDZ); it is a noop until the drag section wires it.
      reapplySelectionAfterSync();
    }

    let reapplySelectionAfterSync: () => void = () => {};

    // Initial sync
    syncLayouts();

    // Flag to suppress sync during active drag (prevents expensive regeneration)
    let isDragging = false;
    let syncPending = false;

    // ── Subscriptions ────────────────────────────────────────────────────
    // Listen to layouts array changes AND the deprecated alias for parity
    // with pre-F-01A projects that haven't been normalized yet. Either one
    // changing triggers a sync.
    const unsub1 = useProjectStore.subscribe((state, prevState) => {
      if (
        state.data.siteLayouts !== prevState.data.siteLayouts ||
        state.data.siteLayout !== prevState.data.siteLayout
      ) {
        if (isDragging) { syncPending = true; return; }
        syncLayouts();
      }
    });

    const unsub2 = useMapStore.subscribe((state, prevState) => {
      if (
        state.layerVisibility.layoutLabels !== prevState.layerVisibility.layoutLabels ||
        state.layerVisibility.layoutImage !== prevState.layerVisibility.layoutImage ||
        state.layerVisibility.stringLines !== prevState.layerVisibility.stringLines ||
        state.layoutZoomOverride !== prevState.layoutZoomOverride ||
        state.layoutLabelLevels !== prevState.layoutLabelLevels
      ) {
        // Same drag gate as unsub1: a mid-drag sync would rebuild the main
        // sources WITH the features that currently live in the scratch layer,
        // rendering every dragged feature twice until dragend.
        if (isDragging) { syncPending = true; return; }
        syncLayouts();
      }
    });

    // ── Drag/click handlers ──────────────────────────────────────────────
    //
    // Single shared mutable drag state — only one drag can be in flight at
    // a time, and the per-layout handler wrappers route the click to the
    // owning layout BEFORE the inner handler reads `data.siteLayout`. The
    // alias swap is synchronous (Zustand `set` is sync), so the existing
    // pre-F-01C edit code transparently operates on the clicked layout.

    const map = mapRef.current;

    const drag = {
      entityId: null as string | null,
      startX: 0,
      startY: 0,
      startLng: 0,
      startLat: 0,
      dragging: false,
      /** ONLY the moving features — they live in the scratch source for the
       *  drag's duration; parallel array of their pre-drag coordinates. */
      scratchFeatures: [] as GeoJSON.Feature[],
      scratchOriginals: [] as [number, number][],
      pendingLngLat: null as { lng: number; lat: number } | null,
      ownerLayoutId: null as string | null,
      ownerLabelsLayerId: null as string | null,
    };

    let labelCaptured = false;

    function onLabelMouseDownInner(e: MapMouseEvent, ownerLayoutId: string, ownerLabelsLayerId: string) {
      if (useUIStore.getState().layoutLabelAddMode) return;
      const features = e.features;
      if (!features || features.length === 0 || !map) return;
      const entityId = features[0].properties?.entityId;
      if (!entityId) return;
      // In select mode, a mousedown on an ALREADY-SELECTED label arms a
      // multi-drag of the selection — that's the whole point of selecting.
      // On anything else, stand aside for the rectangle tool. (The
      // unconditional early-return here is what made rect-select-then-drag
      // pan the map instead of moving the labels.)
      if (useUIStore.getState().layoutLabelSelectMode
        && !useUIStore.getState().selectedLayoutEntityIds.includes(entityId)) {
        return;
      }

      e.preventDefault();
      labelCaptured = true;
      requestAnimationFrame(() => { labelCaptured = false; });

      drag.entityId = entityId;
      drag.startX = e.point.x;
      drag.startY = e.point.y;
      drag.startLng = e.lngLat.lng;
      drag.startLat = e.lngLat.lat;
      drag.dragging = false;
      drag.scratchFeatures = [];
      drag.scratchOriginals = [];
      drag.pendingLngLat = null;
      drag.ownerLayoutId = ownerLayoutId;
      drag.ownerLabelsLayerId = ownerLabelsLayerId;

      map.on('mousemove', onDragMove);
      // WINDOW-level, capture-phase — NOT map.once('mouseup'). Mapbox's map
      // 'mouseup' fires only for releases over the canvas; releasing over a
      // side panel would strand the whole drag choreography.
      window.addEventListener('mouseup', onLabelWindowMouseUp, true);
    }

    // ── Drag visual mode (A/B under owner evaluation) ────────────────────
    //
    // 'live'  — the real features move per frame via updateData. Perfect
    //           fidelity; tracking trails the worker's tile rebuild by a
    //           beat ("small manageable delay" — owner, on real GPU).
    // 'ghost' — a screen-space stand-in follows the cursor at compositor
    //           speed (deterministic on ANY machine); the real features move
    //           once, at release, through the same commit path.
    // Flip in the console: localStorage.sl_drag_mode = 'ghost' (or 'live'),
    // then just start the next drag — read per drag, no reload needed.
    // The loser gets deleted after the owner's verdict.
    function dragVisualMode(): 'live' | 'ghost' {
      // A/B settled 2026-08-25: the owner adopted the ghost ("working
      // perfectly") — deterministic cursor tracking on any machine beats
      // live tracking's tile-rebuild lag. The live path's per-frame flushes
      // are deleted; the real features move ONCE, at release (see the
      // usedGhost blocks in the finalize functions).
      return 'ghost';
    }

    /** Screen-space drag ghost. One absolutely-positioned container over the
     *  map, translated per mousemove — never touches the mapbox pipeline.
     *  `rebase` redraws the ghost's children from GEOMETRY at the current
     *  camera (set by each drag's creator); `lastPt` is the cursor's latest
     *  screen position, the anchor a camera-move rebase works from. */
    const ghost = {
      el: null as HTMLDivElement | null,
      startX: 0,
      startY: 0,
      lastPt: null as { x: number; y: number } | null,
      rebase: null as ((dLng: number, dLat: number) => void) | null,
    };
    function ghostDestroy() {
      ghost.el?.remove();
      ghost.el = null;
      ghost.rebase = null;
      ghost.lastPt = null;
    }
    /** A committed MODULE drag hands its ghost off instead of destroying it:
     *  the grid source is non-dynamic (see addSource), so the commit setData
     *  re-tiles for ~1s on huge grids — the ghost stands in at the drop
     *  position until the tiles land, making the commit feel INSTANT (owner:
     *  drag responsiveness must never regress; it was the point of the drag
     *  rework). The mid-drag visual grammar (originals + ghost) simply
     *  persists for the settle window. */
    let settlingGhostEl: HTMLDivElement | null = null;
    function destroySettlingGhost() {
      settlingGhostEl?.remove();
      settlingGhostEl = null;
    }
    function ghostRelease(): HTMLDivElement | null {
      const el = ghost.el;
      ghost.el = null;
      ghost.rebase = null;
      ghost.lastPt = null;
      return el;
    }
    function ghostCreate(map2: MapboxMap, startPt: { x: number; y: number }) {
      ghostDestroy();
      destroySettlingGhost();
      const el = document.createElement('div');
      el.style.cssText =
        // Full-size, not 0x0: the module ghost's SVG sizes itself to 100% of
        // this box — a zero-size parent collapses it to invisible.
        'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:10;will-change:transform;overflow:visible;';
      map2.getContainer().appendChild(el);
      ghost.el = el;
      ghost.startX = startPt.x;
      ghost.startY = startPt.y;
      ghost.lastPt = { x: startPt.x, y: startPt.y };
      return el;
    }
    function ghostMove(pt: { x: number; y: number }) {
      ghost.lastPt = { x: pt.x, y: pt.y };
      if (ghost.el) {
        ghost.el.style.transform = `translate(${pt.x - ghost.startX}px, ${pt.y - ghost.startY}px)`;
      }
    }

    /** Mid-drag camera motion (wheel zoom for precision, keyboard pan — the
     *  camera stays FREE during drags, owner requirement). The ghost's pixel
     *  anchor and the commit's pendingLngLat both go stale the moment the
     *  camera moves, so on every camera frame: re-derive the geo delta from
     *  the ground now under the cursor, sync the commit anchor to it, and
     *  redraw the ghost from geometry at the new camera. Ghost and landing
     *  share one truth, so no camera motion can split them. */
    function onDragCameraMove() {
      if (!map || !ghost.el || !ghost.rebase || !ghost.lastPt) return;
      const st = drag.dragging ? drag : moduleDrag.dragging ? moduleDrag : null;
      if (!st) return;
      const g = map.unproject([ghost.lastPt.x, ghost.lastPt.y]);
      st.pendingLngLat = { lng: g.lng, lat: g.lat };
      // Reset the translate to identity and re-anchor at the cursor: the
      // rebase draws children at their absolute projected positions, and
      // subsequent mousemoves translate incrementally from here.
      ghost.el.style.transform = 'translate(0px, 0px)';
      ghost.startX = ghost.lastPt.x;
      ghost.startY = ghost.lastPt.y;
      ghost.rebase(g.lng - st.startLng, g.lat - st.startLat);
    }

    // ── Hover suspension during drag ─────────────────────────────────────
    //
    // Every layer-scoped mouseenter/mouseleave registration makes mapbox run
    // a queryRenderedFeatures point-query on EVERY mousemove — 4 queries per
    // layout per move, on the main thread, all through a drag that has no use
    // for hover feedback. Suspending them for the drag's duration removes
    // that tax; safe because syncs (and therefore handler [de]registration)
    // are deferred while isDragging, so the handler set cannot change under
    // the suspension.
    let hoverSuspended = false;
    function suspendHoverHandlers() {
      if (hoverSuspended || !map) return;
      hoverSuspended = true;
      for (const h of handlersRef.current.values()) {
        try {
          map.off('mouseenter', h.labelsLayerId, h.onLabelEnter);
          map.off('mouseleave', h.labelsLayerId, h.onLabelLeave);
          map.off('mouseenter', h.gridFillLayerId, h.onModuleEnter);
          map.off('mouseleave', h.gridFillLayerId, h.onModuleLeave);
        } catch { /* layer may be mid-teardown */ }
      }
    }
    function resumeHoverHandlers() {
      if (!hoverSuspended || !map) return;
      hoverSuspended = false;
      for (const h of handlersRef.current.values()) {
        try {
          map.on('mouseenter', h.labelsLayerId, h.onLabelEnter);
          map.on('mouseleave', h.labelsLayerId, h.onLabelLeave);
          map.on('mouseenter', h.gridFillLayerId, h.onModuleEnter);
          map.on('mouseleave', h.gridFillLayerId, h.onModuleLeave);
        } catch { /* layer may be mid-teardown */ }
      }
    }

    // ── In-place drag via GeoJSONSource.updateData ───────────────────────
    //
    // Fourth mechanism, and the structural one. History, so nobody retries
    // the others: full-collection setData per frame = seconds per frame at
    // grid scale; scratch layers = a worker tile/glyph build that per-frame
    // setData keeps restarting, so the movers rendered NOWHERE for whole
    // drags (harness-proven); filters and feature-state hides both leave
    // multi-hundred-ms duplicate/blink windows (setFilter re-buckets tiles
    // asynchronously; hides need the scratch to be visible first).
    //
    // updateData moves the REAL features inside the main source: the worker
    // patches only the affected tiles, the layer already exists (no cold
    // build), there is nothing to hide and nothing to tear down. Requires
    // feature ids — which is what promoteId on both sources provides.
    // Mapbox's dynamic-source partial update takes a FeatureCollection of the
    // CHANGED features (matched by top-level feature.id and replaced) — not a
    // MapLibre-style {add/update/remove} diff, which it rejects with "Data to
    // update should be a feature or a feature collection."
    function applyFeatureUpdates(sourceId: string, features: GeoJSON.Feature[]) {
      if (!map || features.length === 0) return;
      const src = map.getSource(sourceId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (src && typeof (src as any).updateData === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (src as any).updateData({ type: 'FeatureCollection', features });
      }
    }

    function onDragMove(e: MapMouseEvent) {
      if (!drag.entityId || !map || !drag.ownerLayoutId) return;
      // Self-heal: a release OUTSIDE the browser window delivers no mouseup
      // to the page at all. First movement back over the map reveals the
      // button is no longer down — CANCEL (owner requirement: an off-map
      // release never commits). Gated on `dragging`: mapbox queues DOM events
      // into render frames, so a stale pre-mousedown move with buttons=0 can
      // be processed AFTER the mousedown that armed this drag — firing the
      // self-heal on an armed-but-not-dragging state killed legitimate drags
      // and clicks on slow frames (harness-proven).
      if (drag.dragging && (e.originalEvent.buttons & 1) === 0) {
        finishLabelDrag(null, null, true);
        return;
      }
      const dx = e.point.x - drag.startX;
      const dy = e.point.y - drag.startY;
      if (!drag.dragging) {
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.dragging = true;
        isDragging = true;
        map.dragPan.disable();
        map.on('move', onDragCameraMove);
        map.getCanvas().style.cursor = 'grabbing';
        suspendHoverHandlers();

        // Which labels to move? Resolve from active layout — by the time we
        // reach here, the per-layout handler has already called
        // setActiveLayoutId(ownerLayoutId), so siteLayout === owner layout.
        const selectedIds = useUIStore.getState().selectedLayoutEntityIds;
        const isMulti = selectedIds.length > 1 && selectedIds.includes(drag.entityId!);
        const movingIds = new Set(isMulti ? selectedIds : [drag.entityId!]);
        const layout = getActiveLayout();
        if (layout?.transform) {
          // The features currently rendered live in the geojson cache — no
          // regeneration at dragstart (that regen was the visible reaction
          // lag). Fall back to building only if the cache is somehow cold.
          const sanitized = sanitizeLayoutId(layout.id);
          let original = labelsGeojsonCache.get(sanitized);
          if (!original) {
            const layoutsAll = getEffectiveLayouts();
            const total = layoutsAll.reduce((s, l) => s + (l.textEntities?.length ?? 0), 0);
            const dense = shouldApplyDensityFilter(total);
            const entities = dense
              ? layout.textEntities.filter((ent) => ent.classification?.level !== 'module')
              : layout.textEntities;
            original = entitiesToGeoJSONForLayout(entities, layout.transform, layout.id);
          }
          // The moving features stay IN the main source and get moved in
          // place per frame via updateData. Nothing is created, hidden or
          // duplicated — the clones here only carry geometry + originals for
          // the per-frame math and the cancel restore.
          drag.scratchFeatures = [];
          drag.scratchOriginals = [];
          for (const f of original.features) {
            if (f.properties?.entityId && movingIds.has(f.properties.entityId) && f.geometry.type === 'Point') {
              const pt = f.geometry as GeoJSON.Point;
              drag.scratchOriginals.push([pt.coordinates[0], pt.coordinates[1]]);
              drag.scratchFeatures.push({ ...f, geometry: { type: 'Point' as const, coordinates: [...pt.coordinates] } } as GeoJSON.Feature);
            }
          }
          if (dragVisualMode() === 'ghost') {
            // Screen-space stand-ins at each label's projected position; the
            // originals stay put (standard drag-and-drop idiom) and the real
            // move happens once, at release.
            // Anchor at the MOUSEDOWN point, not this arming mousemove: the
            // children are drawn at their mousedown-time positions, and the
            // commit measures its delta from mousedown too. Anchoring here
            // instead silently ate everything moved before the threshold
            // fired — on a fast flick, tens to hundreds of px of offset
            // between the cursor's grip and where the drop would land
            // (owner-reported: "fast drag shortly after clicking").
            const el = ghostCreate(map, { x: drag.startX, y: drag.startY });
            const labelEls: Array<{ d: HTMLDivElement; lng: number; lat: number }> = [];
            // Mirror the rendered zoom-ladder size (utils/labelZoom.ts) —
            // fixed-size ghosts over zoom-sized symbols read as broken.
            // Floored: a rect-selection can include collision-hidden labels,
            // and their stand-ins must stay visible while dragged.
            const ghostZoom = map.getZoom();
            for (const f of drag.scratchFeatures) {
              const pt = f.geometry as GeoJSON.Point;
              const sp = map.project([pt.coordinates[0], pt.coordinates[1]]);
              const d = document.createElement('div');
              d.textContent = String(f.properties?.text ?? '');
              const color = (LEVEL_COLORS as Record<string, string>)[f.properties?.level] ?? '#9ca3af';
              const size = Math.max(9, labelSizeAt(ghostZoom, f.properties?.level));
              d.style.cssText =
                `position:absolute;left:${sp.x}px;top:${sp.y}px;transform:translate(-50%,-50%);` +
                `font:${size}px "Open Sans",sans-serif;color:${color};white-space:nowrap;` +
                'text-shadow:0 0 2px rgba(0,0,0,0.9),0 0 2px rgba(0,0,0,0.9);';
              el.appendChild(d);
              labelEls.push({ d, lng: pt.coordinates[0], lat: pt.coordinates[1] });
            }
            // With the module grid shown, a dragged STRING label carries its
            // modules (the grid recenters on the label at commit) — so the
            // ghost carries them too, or the user aims the text blind and the
            // modules teleport at release. Same SVG grammar as the module
            // drag's ghost. Pinned modules (modulePositions overrides) do NOT
            // follow the label and are left out.
            const gridPolys: Array<{ poly: SVGPolygonElement; ring: [number, number][] }> = [];
            const fillId = layoutGridFillLayerId(layout.id);
            const fillLayer = map.getLayer(fillId);
            const gridShown = !!fillLayer
              && (map.getLayoutProperty(fillId, 'visibility') ?? 'visible') !== 'none'
              && ghostZoom >= ((fillLayer as { minzoom?: number }).minzoom ?? 0);
            if (gridShown) {
              const movingStringIds = new Set(
                drag.scratchFeatures
                  .filter((f) => f.properties?.level === 'string' && f.properties?.entityId)
                  .map((f) => f.properties!.entityId as string),
              );
              if (movingStringIds.size > 0) {
                const grid = gridGeojsonCache.get(sanitized) ?? generateModuleGridForLayout(layout);
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;');
                for (const gf of grid.features) {
                  const p = gf.properties;
                  if (!p || !movingStringIds.has(p.entityId) || gf.geometry.type !== 'Polygon') continue;
                  if (layout.stringOverrides?.[p.entityId]?.modulePositions?.[p.moduleNumber]) continue;
                  const ring = (gf.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
                  const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
                  poly.setAttribute('points', ring.map((c) => {
                    const sp = map.project([c[0], c[1]]);
                    return `${sp.x},${sp.y}`;
                  }).join(' '));
                  poly.setAttribute('fill', 'rgba(19,214,198,0.15)');
                  poly.setAttribute('stroke', '#13d6c6');
                  poly.setAttribute('stroke-width', '2');
                  svg.appendChild(poly);
                  gridPolys.push({ poly, ring });
                }
                if (gridPolys.length > 0) el.appendChild(svg);
              }
            }
            ghost.rebase = (dLng, dLat) => {
              if (!map) return;
              for (const { d, lng, lat } of labelEls) {
                const sp = map.project([lng + dLng, lat + dLat]);
                d.style.left = `${sp.x}px`;
                d.style.top = `${sp.y}px`;
              }
              for (const { poly, ring } of gridPolys) {
                poly.setAttribute('points', ring.map((c) => {
                  const sp = map.project([c[0] + dLng, c[1] + dLat]);
                  return `${sp.x},${sp.y}`;
                }).join(' '));
              }
            };
          }
        }
      }
      drag.pendingLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      ghostMove(e.point); // compositor-speed; no mapbox involvement
    }

    /**
     * Finalize a label drag from ANY end signal.
     * `cancel` true (off-canvas release, stranded drag, self-heal): the
     * features are restored to their pre-drag positions and NOTHING commits —
     * owner requirement: releasing outside the map means "never mind".
     * `originalEvent` null means a synthesized finalize — never a click, so
     * no selection runs.
     */
    function finishLabelDrag(
      lngLat: { lng: number; lat: number } | null,
      originalEvent: MouseEvent | null,
      cancel = false,
    ) {
      if (!map) return;
      map.off('mousemove', onDragMove);
      window.removeEventListener('mouseup', onLabelWindowMouseUp, true);
      const usedGhost = !!ghost.el;
      // An svg child means the ghost carries module outlines (string label
      // dragged with the grid shown). NOT destroyed yet — a committed drag
      // with a grid ghost hands it off below to bridge the grid re-tile,
      // mirroring finishModuleDrag; every other exit destroys it below.
      const ghostHasGrid = !!ghost.el?.querySelector('svg');
      let ghostHandedOff = false;

      let committed = false;
      if (drag.dragging) {
        map.dragPan.enable();
        map.off('move', onDragCameraMove);
        map.getCanvas().style.cursor = '';
        resumeHoverHandlers();
        const layout = getActiveLayout();
        const commitAt = cancel ? null : (lngLat ?? drag.pendingLngLat);
        if (drag.entityId && layout?.transform && commitAt) {
          const deltaLng = commitAt.lng - drag.startLng;
          const deltaLat = commitAt.lat - drag.startLat;
          if (drag.scratchFeatures.length > 1) {
            const updates: Array<{ id: string; cx: number; cy: number; manuallyMoved?: boolean }> = [];
            for (let i = 0; i < drag.scratchFeatures.length; i++) {
              const orig = drag.scratchOriginals[i];
              if (!orig) continue;
              const newLng = orig[0] + deltaLng;
              const newLat = orig[1] + deltaLat;
              const [pdfX, pdfY] = inverseTransformPoint(layout.transform!, newLng, newLat);
              const eId = drag.scratchFeatures[i]?.properties?.entityId;
              // manuallyMoved: hand-dragged labels leave the DXF anchor set
              if (eId) updates.push({ id: eId, cx: pdfX, cy: pdfY, manuallyMoved: true });
            }
            if (updates.length > 0) {
              useProjectStore.getState().batchUpdateTextEntities(updates);
              committed = true;
            }
          } else {
            // Commit the ANCHOR's new position (orig + delta), never the
            // cursor's: the grab point can sit tens of px off the anchor, and
            // the ghost, the updateData move below, and the cache patch all
            // place the label at orig + delta — a cursor commit persists a
            // position the user never saw, off by the grab offset.
            const orig = drag.scratchOriginals[0];
            const newLng = orig ? orig[0] + deltaLng : commitAt.lng;
            const newLat = orig ? orig[1] + deltaLat : commitAt.lat;
            const [pdfX, pdfY] = inverseTransformPoint(layout.transform, newLng, newLat);
            useProjectStore.getState().updateTextEntity(drag.entityId, { cx: pdfX, cy: pdfY, manuallyMoved: true });
            committed = true;
          }
          if (committed && usedGhost && drag.ownerLayoutId) {
            // Ghost mode never ran per-frame updates — move the real features
            // ONCE now, or the snapshot-advance below would leave them at
            // their origins.
            for (let i = 0; i < drag.scratchFeatures.length; i++) {
              const f = drag.scratchFeatures[i];
              const orig = drag.scratchOriginals[i];
              if (f && f.geometry.type === 'Point' && orig) {
                (f.geometry as GeoJSON.Point).coordinates = [orig[0] + deltaLng, orig[1] + deltaLat];
              }
            }
            applyFeatureUpdates(layoutLabelsSourceId(drag.ownerLayoutId), drag.scratchFeatures);
          }
        }
        if (!committed && drag.ownerLayoutId) {
          // Cancel: snap the features back where they came from — same
          // updateData mechanism, one frame.
          for (let i = 0; i < drag.scratchFeatures.length; i++) {
            const f = drag.scratchFeatures[i];
            const orig = drag.scratchOriginals[i];
            if (f && f.geometry.type === 'Point' && orig) {
              (f.geometry as GeoJSON.Point).coordinates = [orig[0], orig[1]];
            }
          }
          applyFeatureUpdates(layoutLabelsSourceId(drag.ownerLayoutId), drag.scratchFeatures);
        }
      } else if (originalEvent) {
        // Click (no drag) — handle selection
        if (drag.entityId) {
          const isCtrl = originalEvent.ctrlKey || originalEvent.metaKey;
          if (isCtrl) {
            useUIStore.getState().toggleLayoutEntitySelection(drag.entityId);
          } else {
            useUIStore.getState().selectLayoutEntity(drag.entityId);
          }
        }
      }

      const wasDragging = drag.dragging;
      // A committed drag's geometry ALREADY matches the store (updateData
      // moved the real features; the commit wrote the same positions back
      // through the same transform), so the labels rebuild would resend
      // identical data. Advance the memo snapshot to the post-commit refs and
      // the sync skips it. EXCEPTION: a moved STRING-level label must still
      // rebuild the module grid (its modules follow the label), so the
      // snapshot is left alone in that case and the dragend pays one rebuild.
      if (wasDragging && committed) {
        const layoutNow = getActiveLayout();
        const snap = layoutNow ? prevLayoutsRef.current.get(sanitizeLayoutId(layoutNow.id)) : undefined;
        const movedAString = drag.scratchFeatures.some(
          (f) => f?.properties?.level === 'string',
        );
        if (snap && layoutNow && !movedAString) {
          snap.textEntities = layoutNow.textEntities;
          // Patch the cache with final positions so the next dragstart (which
          // reads the cache) starts from where the labels actually are.
          const cached = labelsGeojsonCache.get(sanitizeLayoutId(layoutNow.id));
          if (cached) {
            const finalById = new Map(
              drag.scratchFeatures
                .filter((f) => f.properties?.entityId)
                .map((f) => [f.properties!.entityId as string, f.geometry]),
            );
            for (const cf of cached.features) {
              const fin = cf.properties?.entityId ? finalById.get(cf.properties.entityId) : undefined;
              if (fin) cf.geometry = JSON.parse(JSON.stringify(fin));
            }
          }
        }
      }
      if (committed && ghostHasGrid) {
        // Hand the ghost off to bridge the dragend grid rebuild (the string's
        // modules follow the label, and the grid source is non-dynamic — see
        // finishModuleDrag for the grammar). The text stand-ins are dropped
        // first: the real labels already moved via updateData above, so only
        // the module outlines need to stand in.
        settlingGhostEl = ghostRelease();
        if (settlingGhostEl) {
          ghostHandedOff = true;
          settlingGhostEl.querySelectorAll('div').forEach((d) => d.remove());
        }
      }
      if (!ghostHandedOff) ghostDestroy();
      drag.entityId = null;
      drag.dragging = false;
      drag.scratchFeatures = [];
      drag.scratchOriginals = [];
      drag.pendingLngLat = null;
      drag.ownerLayoutId = null;
      drag.ownerLabelsLayerId = null;
      isDragging = false;
      if (wasDragging) {
        syncPending = false;
        syncLayouts();
      } else if (syncPending) { syncPending = false; syncLayouts(); }
      if (ghostHandedOff) {
        // Registered AFTER the sync so the safety timeout clocks the async
        // re-tile, not the main-thread grid regen the sync just paid (that
        // regen can eat most of the window on a huge grid). Removal triggers
        // mirror finishModuleDrag: tiles landed, camera moved, or timeout.
        const done = () => destroySettlingGhost();
        map.once('idle', done);
        map.once('move', done);
        window.setTimeout(done, 2500);
      }
    }

    /** Window-level dragend: fires for releases over side panels too, where
     *  mapbox's own map 'mouseup' never would. A release ON the canvas
     *  commits at the last on-map position; a release anywhere else CANCELS
     *  (owner requirement — the features snap back). */
    function onLabelWindowMouseUp(ev: MouseEvent) {
      if (!map) { finishLabelDrag(null, ev, true); return; }
      const rect = map.getCanvas().getBoundingClientRect();
      const inside =
        ev.clientX >= rect.left && ev.clientX <= rect.right &&
        ev.clientY >= rect.top && ev.clientY <= rect.bottom &&
        document.elementFromPoint(ev.clientX, ev.clientY)?.closest('.mapboxgl-map') != null;
      finishLabelDrag(null, ev, !inside);
    }

    /** Finalize any drag left armed by an out-of-window release (no in-page
     *  mouseup ever fires for those). Synthesized finalizes CANCEL — the user
     *  never released over the map, so nothing may commit. MUST be called
     *  BEFORE any setActiveLayoutId swap so a (hypothetical) commit could
     *  never write into the newly clicked layout. */
    function finalizeStaleDrags() {
      if (drag.entityId) finishLabelDrag(null, null, true);
      if (moduleDrag.entityId) finishModuleDrag(null, null, true);
    }

    function onLabelMouseEnterShared() {
      if (map && !useUIStore.getState().layoutLabelAddMode) {
        map.getCanvas().style.cursor = 'pointer';
      }
    }
    function onLabelMouseLeaveShared() {
      if (map && !drag.dragging && !useUIStore.getState().layoutLabelAddMode) {
        map.getCanvas().style.cursor = '';
      }
    }

    // ── Add-label-mode click on empty map ────────────────────────────────
    function onAddLabelClick(e: MapMouseEvent) {
      if (!useUIStore.getState().layoutLabelAddMode) return;
      e.preventDefault();
      const { lng, lat } = e.lngLat;
      useUIStore.setState({ layoutLabelAddMode: false, newLabelPosition: { lng, lat } });
    }

    // ── Module grid drag / selection ────────────────────────────────────
    const moduleSel = {
      entityId: null as string | null,
      modules: new Set<number>(),
      lastClicked: null as number | null,
      ownerLayoutId: null as string | null,
    };
    const moduleDrag = {
      entityId: null as string | null,
      moduleNumber: null as number | null,
      startX: 0,
      startY: 0,
      startLng: 0,
      startLat: 0,
      dragging: false,
      /** ONLY the moving module polygons — carried by the grid scratch source. */
      scratchFeatures: [] as GeoJSON.Feature[],
      originalCoords: new Map<number, [number, number][]>(),
      originalCenters: new Map<number, [number, number]>(),
      pendingLngLat: null as { lng: number; lat: number } | null,
      ownerLayoutId: null as string | null,
    };

    function polygonCenter(ring: [number, number][]): [number, number] {
      const count = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        ? ring.length - 1 : ring.length;
      let sumLng = 0, sumLat = 0;
      for (let i = 0; i < count; i++) {
        sumLng += ring[i][0];
        sumLat += ring[i][1];
      }
      return [sumLng / count, sumLat / count];
    }

    // fsids currently painted selected. Tracked explicitly because mapbox
    // REJECTS removeFeatureState({source}, key) without an id ("A feature id
    // is required to remove its specific state property" — harness-captured
    // via map.on('error')), so every blanket deselect was silently a no-op:
    // the stuck teal outlines the owner kept seeing.
    const appliedSelectionIds = new Set<string>();
    let appliedSelectionSourceId: string | null = null;

    function clearAppliedSelection() {
      if (map && appliedSelectionSourceId && map.getSource(appliedSelectionSourceId)) {
        for (const id of appliedSelectionIds) {
          try {
            map.removeFeatureState({ source: appliedSelectionSourceId, id }, 'selected');
          } catch { /* source mid-teardown */ }
        }
      }
      appliedSelectionIds.clear();
      appliedSelectionSourceId = null;
    }

    function applySelectionHighlight() {
      // Per-id clear of whatever is currently painted, then per-id set of the
      // current selection — O(selection) both ways, valid API both ways.
      clearAppliedSelection();
      if (!map || !moduleSel.ownerLayoutId || !moduleSel.entityId) return;
      const sourceId = layoutGridSourceId(moduleSel.ownerLayoutId);
      if (!map.getSource(sourceId)) return;
      for (const modNum of moduleSel.modules) {
        const id = `${moduleSel.entityId}:${modNum}`;
        map.setFeatureState({ source: sourceId, id }, { selected: true });
        appliedSelectionIds.add(id);
      }
      appliedSelectionSourceId = sourceId;
    }
    // Wire the late-bound hook now that moduleSel exists (see syncLayouts).
    // Grid-select highlight rides the same hook: a source recreate wipes
    // feature-state, so both selections repaint after every sync.
    reapplySelectionAfterSync = () => {
      applySelectionHighlight();
      applyGridSelHighlight();
    };

    function clearModuleSelection() {
      if (moduleSel.modules.size === 0) return;
      moduleSel.entityId = null;
      moduleSel.modules.clear();
      moduleSel.lastClicked = null;
      moduleSel.ownerLayoutId = null;
      clearAppliedSelection();
    }

    // ── Grid-select mode: whole-string selection highlight + batch drag ──
    // "Select Grid" lassoes STRINGS by their cells; dragging any selected
    // cell moves every selected string's grid as a unit by writing per-string
    // gridOffsetAlongM/PerpM (REPLACE semantics — never module pins). Same
    // per-id feature-state doctrine as applySelectionHighlight above.
    const gridSelApplied = new Set<string>();
    let gridSelSourceId: string | null = null;

    function clearGridSelHighlight() {
      if (map && gridSelSourceId && map.getSource(gridSelSourceId)) {
        for (const id of gridSelApplied) {
          try {
            map.removeFeatureState({ source: gridSelSourceId, id }, 'selected');
          } catch { /* source mid-teardown */ }
        }
      }
      gridSelApplied.clear();
      gridSelSourceId = null;
    }

    function applyGridSelHighlight() {
      clearGridSelHighlight();
      if (!map || !useUIStore.getState().layoutGridSelectMode) return;
      const selected = useUIStore.getState().selectedLayoutEntityIds;
      if (selected.length === 0) return;
      const layout = getActiveLayout();
      if (!layout) return;
      const sanitized = sanitizeLayoutId(layout.id);
      const sourceId = layoutGridSourceId(sanitized);
      if (!map.getSource(sourceId)) return;
      const sel = new Set(selected);
      const cached = gridGeojsonCache.get(sanitized) ?? generateModuleGridForLayout(layout);
      for (const f of cached.features) {
        const p = f.properties!;
        if (!sel.has(p.entityId)) continue;
        const id = `${p.entityId}:${p.moduleNumber}`;
        map.setFeatureState({ source: sourceId, id }, { selected: true });
        gridSelApplied.add(id);
      }
      gridSelSourceId = sourceId;
    }

    const gridDrag = {
      entityIds: [] as string[],
      startX: 0,
      startY: 0,
      startLng: 0,
      startLat: 0,
      dragging: false,
      pendingLngLat: null as { lng: number; lat: number } | null,
    };

    function startGridDrag(e: MapMouseEvent) {
      if (!map) return;
      gridDrag.entityIds = [...useUIStore.getState().selectedLayoutEntityIds];
      gridDrag.startX = e.point.x;
      gridDrag.startY = e.point.y;
      gridDrag.startLng = e.lngLat.lng;
      gridDrag.startLat = e.lngLat.lat;
      gridDrag.dragging = false;
      gridDrag.pendingLngLat = null;
      map.on('mousemove', onGridDragMove);
      window.addEventListener('mouseup', onGridWindowMouseUp, true);
    }

    function onGridDragMove(e: MapMouseEvent) {
      if (gridDrag.entityIds.length === 0 || !map) return;
      // Self-heal after an out-of-window release — see onModuleDragMove.
      if (gridDrag.dragging && (e.originalEvent.buttons & 1) === 0) {
        finishGridDrag(null, true);
        return;
      }
      const dx = e.point.x - gridDrag.startX;
      const dy = e.point.y - gridDrag.startY;
      if (!gridDrag.dragging) {
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
        gridDrag.dragging = true;
        isDragging = true;
        map.dragPan.disable();
        map.on('move', onDragCameraMove);
        map.getCanvas().style.cursor = 'grabbing';
        suspendHoverHandlers();
        // Ghost: ONE bounding rectangle per selected string (a cell-per-cell
        // ghost at 50×27 features would fight the frame budget for nothing).
        const layout = getActiveLayout();
        if (layout) {
          const sanitized = sanitizeLayoutId(layout.id);
          const cached = gridGeojsonCache.get(sanitized) ?? generateModuleGridForLayout(layout);
          const sel = new Set(gridDrag.entityIds);
          const boxes = new Map<string, { minLng: number; minLat: number; maxLng: number; maxLat: number }>();
          for (const f of cached.features) {
            const p = f.properties!;
            if (!sel.has(p.entityId) || f.geometry.type !== 'Polygon') continue;
            const ring = (f.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
            let box = boxes.get(p.entityId);
            if (!box) {
              box = { minLng: Infinity, minLat: Infinity, maxLng: -Infinity, maxLat: -Infinity };
              boxes.set(p.entityId, box);
            }
            for (const [clng, clat] of ring) {
              if (clng < box.minLng) box.minLng = clng;
              if (clng > box.maxLng) box.maxLng = clng;
              if (clat < box.minLat) box.minLat = clat;
              if (clat > box.maxLat) box.maxLat = clat;
            }
          }
          // Ghost-ONLY visual (no live mode): a whole-grid drag re-tiles the
          // full source on commit — per-frame updateData at this scale is
          // exactly the drag lag the ghost doctrine exists to avoid.
          {
            const el = ghostCreate(map, { x: gridDrag.startX, y: gridDrag.startY });
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;');
            const svgPolys: Array<{ poly: SVGPolygonElement; ring: [number, number][] }> = [];
            for (const box of boxes.values()) {
              const ring: [number, number][] = [
                [box.minLng, box.minLat],
                [box.maxLng, box.minLat],
                [box.maxLng, box.maxLat],
                [box.minLng, box.maxLat],
              ];
              const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
              poly.setAttribute('points', ring.map((c) => {
                const sp = map.project([c[0], c[1]]);
                return `${sp.x},${sp.y}`;
              }).join(' '));
              poly.setAttribute('fill', 'rgba(19,214,198,0.12)');
              poly.setAttribute('stroke', '#13d6c6');
              poly.setAttribute('stroke-width', '2');
              svg.appendChild(poly);
              svgPolys.push({ poly, ring });
            }
            el.appendChild(svg);
            ghost.rebase = (dLng2, dLat2) => {
              if (!map) return;
              for (const { poly, ring } of svgPolys) {
                poly.setAttribute('points', ring.map((c) => {
                  const sp = map.project([c[0] + dLng2, c[1] + dLat2]);
                  return `${sp.x},${sp.y}`;
                }).join(' '));
              }
            };
          }
        }
      }
      gridDrag.pendingLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      ghostMove(e.point);
    }

    function finishGridDrag(lngLat: { lng: number; lat: number } | null, cancel = false) {
      if (!map) return;
      map.off('mousemove', onGridDragMove);
      window.removeEventListener('mouseup', onGridWindowMouseUp, true);
      const usedGhost = !!ghost.el;
      let ghostHandedOff = false;
      const wasDragging = gridDrag.dragging;
      if (wasDragging) {
        map.dragPan.enable();
        map.off('move', onDragCameraMove);
        map.getCanvas().style.cursor = '';
        resumeHoverHandlers();
        const commitAt = cancel ? null : (lngLat ?? gridDrag.pendingLngLat);
        const layout = getActiveLayout();
        if (commitAt && layout?.moduleGridConfig && layout.transform) {
          const dLng = commitAt.lng - gridDrag.startLng;
          const dLat = commitAt.lat - gridDrag.startLat;
          if (dLng !== 0 || dLat !== 0) {
            const cfg = layout.moduleGridConfig;
            const byId = new Map(layout.textEntities.map((t) => [t.id, t]));
            const patches: Record<string, Partial<StringOverride>> = {};
            for (const id of gridDrag.entityIds) {
              const ent = byId.get(id);
              if (!ent) continue;
              const frame = stringGridFrame(layout, ent);
              if (!frame) continue;
              // One map delta, projected into EACH string's along/perp frame
              // (strings at different rotations shift by different offsets).
              const { dLng: degPerMLng, dLat: degPerMLat } = metersToDeg(1, 1, frame.gridLat);
              const dxm = dLng / degPerMLng;
              const dym = dLat / degPerMLat;
              const a = (frame.directionDeg * Math.PI) / 180;
              const dAlong = dxm * Math.cos(a) + dym * Math.sin(a);
              const dPerp = -dxm * Math.sin(a) + dym * Math.cos(a);
              const ovr = layout.stringOverrides?.[id];
              const along = (ovr?.gridOffsetAlongM ?? cfg.gridOffsetAlongM ?? 0) + dAlong;
              const perp = (ovr?.gridOffsetPerpM ?? cfg.gridOffsetPerpM ?? 0) + dPerp;
              patches[id] = {
                gridOffsetAlongM: Math.round(along * 1000) / 1000,
                gridOffsetPerpM: Math.round(perp * 1000) / 1000,
              };
            }
            if (Object.keys(patches).length > 0) {
              useProjectStore.getState().batchApplyStringOverridePatches(patches);
              // Offsets change COMPUTED geometry — the dragend regen must
              // run (unlike the module-pin path, which patches the cache).
              // The ghost stands in while it does.
              if (usedGhost) {
                settlingGhostEl = ghostRelease();
                if (settlingGhostEl) {
                  ghostHandedOff = true;
                  const done = () => destroySettlingGhost();
                  map.once('idle', done);
                  window.setTimeout(done, 4000);
                }
              }
            }
          }
        }
      }
      if (!ghostHandedOff) ghostDestroy();
      gridDrag.entityIds = [];
      gridDrag.dragging = false;
      gridDrag.pendingLngLat = null;
      isDragging = false;
      if (wasDragging) {
        syncPending = false;
        syncLayouts();
      } else if (syncPending) { syncPending = false; syncLayouts(); }
    }

    function onGridWindowMouseUp(ev: MouseEvent) {
      if (!map) { finishGridDrag(null, true); return; }
      const rect = map.getCanvas().getBoundingClientRect();
      const inside =
        ev.clientX >= rect.left && ev.clientX <= rect.right &&
        ev.clientY >= rect.top && ev.clientY <= rect.bottom &&
        document.elementFromPoint(ev.clientX, ev.clientY)?.closest('.mapboxgl-map') != null;
      finishGridDrag(null, !inside);
    }

    function onModuleMouseDownInner(e: MapMouseEvent, ownerLayoutId: string) {
      if (labelCaptured) return;
      if (useUIStore.getState().layoutLabelAddMode) return;
      // Select mode is a LABELS workflow: a module under a label must never
      // hijack the gesture and silently move (harness-proven stringOverride
      // corruption).
      if (useUIStore.getState().layoutLabelSelectMode) return;
      // Grid-select mode: a SELECTED cell arms the whole-grid batch drag;
      // everything else falls through to the map-level lasso. The module-pin
      // drag NEVER runs in this mode.
      if (useUIStore.getState().layoutGridSelectMode) {
        const hitId = e.features?.[0]?.properties?.entityId;
        if (hitId && useUIStore.getState().selectedLayoutEntityIds.includes(hitId)) {
          e.preventDefault();
          startGridDrag(e);
        }
        return;
      }
      const features = e.features;
      if (!features || features.length === 0 || !map) return;
      const entityId = features[0].properties?.entityId;
      const moduleNumber = features[0].properties?.moduleNumber;
      if (!entityId || moduleNumber == null) return;
      e.preventDefault();
      moduleDrag.entityId = entityId;
      moduleDrag.moduleNumber = moduleNumber;
      moduleDrag.startX = e.point.x;
      moduleDrag.startY = e.point.y;
      moduleDrag.startLng = e.lngLat.lng;
      moduleDrag.startLat = e.lngLat.lat;
      moduleDrag.dragging = false;
      moduleDrag.scratchFeatures = [];
      moduleDrag.originalCoords.clear();
      moduleDrag.originalCenters.clear();
      moduleDrag.pendingLngLat = null;
      moduleDrag.ownerLayoutId = ownerLayoutId;
      map.on('mousemove', onModuleDragMove);
      // Window-level, not map.once('mouseup') — see onLabelMouseDownInner.
      window.addEventListener('mouseup', onModuleWindowMouseUp, true);
    }

    function onModuleDragMove(e: MapMouseEvent) {
      if (!moduleDrag.entityId || moduleDrag.moduleNumber == null || !map) return;
      // Self-heal after an out-of-window release — CANCEL; gated on
      // `dragging` because mapbox's queued events can replay a stale
      // buttons=0 move after the arming mousedown (see onDragMove).
      if (moduleDrag.dragging && (e.originalEvent.buttons & 1) === 0) {
        finishModuleDrag(null, null, true);
        return;
      }
      const dx = e.point.x - moduleDrag.startX;
      const dy = e.point.y - moduleDrag.startY;
      if (!moduleDrag.dragging) {
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
        moduleDrag.dragging = true;
        isDragging = true;
        map.dragPan.disable();
        map.on('move', onDragCameraMove);
        map.getCanvas().style.cursor = 'grabbing';
        suspendHoverHandlers();
        const layout = getActiveLayout();
        if (layout?.moduleGridConfig && layout.transform) {
          // The rendered grid lives in the geojson cache — regenerating it
          // here (~1s+ at grid scale on a weak machine) was the reaction lag
          // the owner felt on every module drag.
          const sanitized = sanitizeLayoutId(layout.id);
          const original = gridGeojsonCache.get(sanitized) ?? generateModuleGridForLayout(layout);
          const movingModules = (moduleSel.entityId === moduleDrag.entityId && moduleSel.ownerLayoutId === layout.id && moduleSel.modules.has(moduleDrag.moduleNumber))
            ? moduleSel.modules
            : new Set([moduleDrag.moduleNumber]);
          // The moving modules stay IN the grid source and are moved in place
          // per frame via updateData — nothing created, hidden or duplicated.
          // Clones carry geometry + originals for the per-frame math and the
          // cancel restore.
          moduleDrag.scratchFeatures = [];
          for (const f of original.features) {
            const p = f.properties!;
            if (p.entityId === moduleDrag.entityId && movingModules.has(p.moduleNumber) && f.geometry.type === 'Polygon') {
              const origRing = (f.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
              moduleDrag.originalCoords.set(p.moduleNumber, origRing.map((c) => [c[0], c[1]]));
              moduleDrag.originalCenters.set(p.moduleNumber, polygonCenter(origRing));
              const clonedRing: [number, number][] = origRing.map((c) => [c[0], c[1]]);
              moduleDrag.scratchFeatures.push({ ...f, geometry: { type: 'Polygon' as const, coordinates: [clonedRing] } });
            }
          }
          if (dragVisualMode() === 'ghost') {
            // SVG outlines from the PROJECTED rings — faithful rotation/size.
            // MOUSEDOWN anchor — see the label mirror for why (fast-flick
            // offset between grip and landing otherwise).
            const el = ghostCreate(map, { x: moduleDrag.startX, y: moduleDrag.startY });
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;');
            const svgPolys: Array<{ poly: SVGPolygonElement; ring: [number, number][] }> = [];
            for (const f of moduleDrag.scratchFeatures) {
              if (f.geometry.type !== 'Polygon') continue;
              const ring = (f.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
              const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
              poly.setAttribute('points', ring.map((c) => {
                const sp = map.project([c[0], c[1]]);
                return `${sp.x},${sp.y}`;
              }).join(' '));
              poly.setAttribute('fill', 'rgba(19,214,198,0.15)');
              poly.setAttribute('stroke', '#13d6c6');
              poly.setAttribute('stroke-width', '2');
              svg.appendChild(poly);
              svgPolys.push({ poly, ring });
            }
            el.appendChild(svg);
            ghost.rebase = (dLng, dLat) => {
              if (!map) return;
              for (const { poly, ring } of svgPolys) {
                poly.setAttribute('points', ring.map((c) => {
                  const sp = map.project([c[0] + dLng, c[1] + dLat]);
                  return `${sp.x},${sp.y}`;
                }).join(' '));
              }
            };
          }
        }
      }
      moduleDrag.pendingLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      ghostMove(e.point);
    }

    /** Module-drag finalize — same contract as finishLabelDrag. */
    function finishModuleDrag(
      lngLat: { lng: number; lat: number } | null,
      originalEvent: MouseEvent | null,
      cancel = false,
    ) {
      if (!map) return;
      map.off('mousemove', onModuleDragMove);
      window.removeEventListener('mouseup', onModuleWindowMouseUp, true);
      const usedGhost = !!ghost.el;
      // NOT destroyed here: a committed drag hands the ghost off to stand in
      // while the non-dynamic source re-tiles (see ghostRelease); every other
      // exit destroys it below.
      let ghostHandedOff = false;
      let committed = false;
      if (moduleDrag.dragging) {
        map.dragPan.enable();
        map.off('move', onDragCameraMove);
        map.getCanvas().style.cursor = '';
        resumeHoverHandlers();
        const layout = getActiveLayout();
        const commitAt = cancel ? null : (lngLat ?? moduleDrag.pendingLngLat);
        if (moduleDrag.entityId && layout && commitAt) {
          const deltaLng = commitAt.lng - moduleDrag.startLng;
          const deltaLat = commitAt.lat - moduleDrag.startLat;
          const existing = layout.stringOverrides?.[moduleDrag.entityId];
          const modulePositions = { ...(existing?.modulePositions ?? {}) };
          for (const [modNum, origCenter] of moduleDrag.originalCenters) {
            modulePositions[modNum] = [origCenter[0] + deltaLng, origCenter[1] + deltaLat];
          }
          useProjectStore.getState().setStringOverride(moduleDrag.entityId, {
            ...existing,
            modulePositions,
          });
          committed = true;
          if (usedGhost && moduleDrag.ownerLayoutId) {
            // Move the scratch clones to their final geometry — this feeds
            // the cache patch below (finalByFsid), which is what the commit
            // setData renders from. The real features never move during the
            // drag (non-dynamic source), so cancel needs no restore.
            for (const f of moduleDrag.scratchFeatures) {
              const origCoords = moduleDrag.originalCoords.get(f.properties?.moduleNumber);
              if (origCoords && f.geometry.type === 'Polygon') {
                (f.geometry as GeoJSON.Polygon).coordinates = [
                  origCoords.map((c) => [c[0] + deltaLng, c[1] + deltaLat]),
                ];
              }
            }
          }
        }
      } else if (originalEvent) {
        // Click (no drag) — toggle selection (scoped to owner layout)
        if (moduleDrag.entityId && moduleDrag.moduleNumber != null && moduleDrag.ownerLayoutId) {
          const isCtrl = originalEvent.ctrlKey || originalEvent.metaKey;
          const isShift = originalEvent.shiftKey;
          // If the selection owner is a different layout, reset.
          if (moduleSel.ownerLayoutId !== moduleDrag.ownerLayoutId || moduleSel.entityId !== moduleDrag.entityId) {
            // Reset highlight on previous owner if any.
            if (moduleSel.ownerLayoutId && moduleSel.ownerLayoutId !== moduleDrag.ownerLayoutId) {
              // The applied-ids tracker remembers the previous owner's source,
              // so the trailing applySelectionHighlight clears it correctly.
              moduleSel.modules.clear();
              moduleSel.entityId = null;
              moduleSel.ownerLayoutId = null;
            }
            moduleSel.entityId = moduleDrag.entityId;
            moduleSel.ownerLayoutId = moduleDrag.ownerLayoutId;
            moduleSel.modules.clear();
            moduleSel.modules.add(moduleDrag.moduleNumber);
            moduleSel.lastClicked = moduleDrag.moduleNumber;
          } else if (isShift && moduleSel.lastClicked != null) {
            const from = Math.min(moduleSel.lastClicked, moduleDrag.moduleNumber);
            const to = Math.max(moduleSel.lastClicked, moduleDrag.moduleNumber);
            for (let m = from; m <= to; m++) {
              moduleSel.modules.add(m);
            }
          } else if (isCtrl) {
            if (moduleSel.modules.has(moduleDrag.moduleNumber)) {
              moduleSel.modules.delete(moduleDrag.moduleNumber);
              if (moduleSel.modules.size === 0) {
                // Deselect-to-empty is safe now: the trailing
                // applySelectionHighlight clears via the applied-ids tracker,
                // which does not depend on the (nulled) owner.
                moduleSel.entityId = null;
                moduleSel.ownerLayoutId = null;
              }
            } else {
              moduleSel.modules.add(moduleDrag.moduleNumber);
            }
            moduleSel.lastClicked = moduleDrag.moduleNumber;
          } else {
            if (moduleSel.modules.size === 1 && moduleSel.modules.has(moduleDrag.moduleNumber)) {
              moduleSel.entityId = null;
              moduleSel.modules.clear();
              moduleSel.lastClicked = null;
              moduleSel.ownerLayoutId = null;
            } else {
              moduleSel.modules.clear();
              moduleSel.modules.add(moduleDrag.moduleNumber);
              moduleSel.lastClicked = moduleDrag.moduleNumber;
            }
          }
          applySelectionHighlight();
        }
      }
      const wasDragging = moduleDrag.dragging;
      // A committed module drag's rendered geometry already matches what the
      // grid would regenerate (the override positions ARE the dragged
      // centers), so advance the memo snapshot and skip the multi-second
      // dragend rebuild entirely.
      if (wasDragging && committed) {
        const layoutNow = getActiveLayout();
        const snap = layoutNow ? prevLayoutsRef.current.get(sanitizeLayoutId(layoutNow.id)) : undefined;
        if (snap && layoutNow) {
          snap.stringOverrides = layoutNow.stringOverrides;
          // Patch the cache with the final geometries so the NEXT dragstart
          // (which reads the cache) sees where the modules actually are —
          // a stale cache would snap them back to pre-drag positions.
          const cached = gridGeojsonCache.get(sanitizeLayoutId(layoutNow.id));
          if (cached) {
            const finalByFsid = new Map(
              moduleDrag.scratchFeatures
                .filter((f) => f.properties?.fsid)
                .map((f) => [f.properties!.fsid as string, f.geometry]),
            );
            for (const cf of cached.features) {
              const fin = cf.properties?.fsid ? finalByFsid.get(cf.properties.fsid) : undefined;
              if (fin) cf.geometry = JSON.parse(JSON.stringify(fin));
            }
            // Non-dynamic sources (see addSource): the commit reaches the
            // screen through ONE setData from the patched cache, and the
            // lines re-derive from it so the dragged string's line follows
            // its modules. On a huge grid the worker re-tile settles about a
            // second after release — the price of the tiler that renders
            // EVERYTHING.
            if (moduleDrag.ownerLayoutId) {
              const gsrc = map.getSource(layoutGridSourceId(moduleDrag.ownerLayoutId));
              if (gsrc && 'setData' in gsrc) (gsrc as GeoJSONSource).setData(cached);
              const textMap = new Map<string, string>();
              for (const e of layoutNow.textEntities ?? []) {
                if (e.id && e.text) textMap.set(e.id, e.text);
              }
              const lsrc = map.getSource(layoutStringLinesSourceId(moduleDrag.ownerLayoutId));
              if (lsrc && 'setData' in lsrc) {
                (lsrc as GeoJSONSource).setData(deriveStringLines(cached, textMap));
              }
              // Hand the ghost off to bridge the re-tile: it already shows
              // the drop position, so the commit reads as instant. Removed
              // on the first of idle (tiles landed), a camera move (screen-
              // anchored ghost would misalign), or a safety timeout.
              if (usedGhost) {
                settlingGhostEl = ghostRelease();
                if (settlingGhostEl) {
                  ghostHandedOff = true;
                  const done = () => destroySettlingGhost();
                  map.once('idle', done);
                  map.once('move', done);
                  window.setTimeout(done, 2500);
                }
              }
            }
          }
        }
      }
      if (!ghostHandedOff) ghostDestroy();
      moduleDrag.entityId = null;
      moduleDrag.moduleNumber = null;
      moduleDrag.dragging = false;
      moduleDrag.scratchFeatures = [];
      moduleDrag.originalCoords.clear();
      moduleDrag.originalCenters.clear();
      moduleDrag.pendingLngLat = null;
      moduleDrag.ownerLayoutId = null;
      isDragging = false;
      if (wasDragging) {
        syncPending = false;
        syncLayouts();
      } else if (syncPending) { syncPending = false; syncLayouts(); }
    }

    /** Window-level module dragend — canvas release commits, anything else
     *  CANCELS (owner requirement). */
    function onModuleWindowMouseUp(ev: MouseEvent) {
      if (!map) { finishModuleDrag(null, ev, true); return; }
      const rect = map.getCanvas().getBoundingClientRect();
      const inside =
        ev.clientX >= rect.left && ev.clientX <= rect.right &&
        ev.clientY >= rect.top && ev.clientY <= rect.bottom &&
        document.elementFromPoint(ev.clientX, ev.clientY)?.closest('.mapboxgl-map') != null;
      finishModuleDrag(null, ev, !inside);
    }

    function onModuleMouseEnterShared() {
      if (map && !useUIStore.getState().layoutLabelAddMode && !drag.dragging && !moduleDrag.dragging) {
        map.getCanvas().style.cursor = 'grab';
      }
    }
    function onModuleMouseLeaveShared() {
      if (map && !drag.dragging && !moduleDrag.dragging && !useUIStore.getState().layoutLabelAddMode) {
        map.getCanvas().style.cursor = '';
      }
    }

    // ── Lasso area selection (queries across ALL labels layers) ──────────
    // The select gesture draws a freehand POLYGON: rows run diagonally on
    // many sites, so an axis-aligned rectangle over-captures neighbors
    // (owner request, 2026-09-01 — OCR-cleanup workflow). A quick straight
    // swipe still behaves like the old rectangle: when the traced path
    // encloses almost no area relative to its bounding box, the selection
    // falls back to the bbox — both muscle memories keep working.
    const rectSel = {
      active: false,
      startX: 0,
      startY: 0,
      points: [] as Array<[number, number]>,
      svg: null as SVGSVGElement | null,
      poly: null as SVGPolygonElement | null,
    };

    const SVG_NS = 'http://www.w3.org/2000/svg';
    const LASSO_MIN_STEP_PX = 4;

    /** Ray-cast in screen space. `ring` is the open lasso path (closed implicitly). */
    function pointInScreenPolygon(px: number, py: number, ring: Array<[number, number]>): boolean {
      let inside = false;
      const n = ring.length;
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = ring[i][0], yi = ring[i][1];
        const xj = ring[j][0], yj = ring[j][1];
        if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      return inside;
    }

    /** Shoelace area of the (implicitly closed) lasso path, in px². */
    function lassoArea(ring: Array<[number, number]>): number {
      let area = 0;
      const n = ring.length;
      for (let i = 0, j = n - 1; i < n; j = i++) {
        area += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
      }
      return Math.abs(area / 2);
    }

    function getAllVisibleLabelsLayerIds(): string[] {
      const ids: string[] = [];
      for (const sanitized of handlersRef.current.keys()) {
        const layerId = layoutLabelsLayerId(sanitized);
        if (mapRef.current?.getLayer(layerId)) ids.push(layerId);
      }
      return ids;
    }

    function getAllVisibleGridFillLayerIds(): string[] {
      const ids: string[] = [];
      for (const sanitized of handlersRef.current.keys()) {
        const layerId = layoutGridFillLayerId(sanitized);
        if (mapRef.current?.getLayer(layerId)) ids.push(layerId);
      }
      return ids;
    }

    function onMapMouseDownForRect(e: MapMouseEvent) {
      const ui = useUIStore.getState();
      const gridMode = ui.layoutGridSelectMode;
      if ((!ui.layoutLabelSelectMode && !gridMode) || !map) return;
      if (gridMode) {
        // A SELECTED grid cell owns the gesture (whole-grid drag); anything
        // else — unselected cell or empty ground — starts a new lasso.
        const gridLayerIds = getAllVisibleGridFillLayerIds();
        if (gridLayerIds.length > 0) {
          const features = map.queryRenderedFeatures(e.point, { layers: gridLayerIds });
          const hitId = features?.[0]?.properties?.entityId;
          if (hitId && ui.selectedLayoutEntityIds.includes(hitId)) {
            e.preventDefault();
            return;
          }
        }
      } else {
        const labelLayerIds = getAllVisibleLabelsLayerIds();
        if (labelLayerIds.length > 0) {
          const features = map.queryRenderedFeatures(e.point, { layers: labelLayerIds });
          if (features && features.length > 0) {
            // A label owns this gesture (drag-of-selection or plain click).
            // Without preventDefault mapbox pans the camera underneath it.
            e.preventDefault();
            return;
          }
        }
      }
      e.preventDefault();
      rectSel.active = true;
      rectSel.startX = e.point.x;
      rectSel.startY = e.point.y;
      rectSel.points = [[e.point.x, e.point.y]];
      const container = map.getContainer();
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.style.position = 'absolute';
      svg.style.inset = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '10';
      const poly = document.createElementNS(SVG_NS, 'polygon');
      poly.setAttribute('fill', 'rgba(19, 214, 198, 0.1)');
      poly.setAttribute('stroke', '#13d6c6');
      poly.setAttribute('stroke-width', '2');
      poly.setAttribute('stroke-dasharray', '6 4');
      svg.appendChild(poly);
      container.appendChild(svg);
      rectSel.svg = svg;
      rectSel.poly = poly;
      map.dragPan.disable();
      map.on('mousemove', onRectDragMove);
      map.once('mouseup', onRectDragUp);
    }

    function onRectDragMove(e: MapMouseEvent) {
      if (!rectSel.active || !rectSel.poly) return;
      const last = rectSel.points[rectSel.points.length - 1];
      const dx = e.point.x - last[0];
      const dy = e.point.y - last[1];
      if (dx * dx + dy * dy >= LASSO_MIN_STEP_PX * LASSO_MIN_STEP_PX) {
        rectSel.points.push([e.point.x, e.point.y]);
        rectSel.poly.setAttribute('points', rectSel.points.map(([x, y]) => `${x},${y}`).join(' '));
      }
    }

    function onRectDragUp(e: MapMouseEvent) {
      if (!map) return;
      map.off('mousemove', onRectDragMove);
      map.dragPan.enable();
      if (rectSel.active && rectSel.svg) {
        const pts = [...rectSel.points, [e.point.x, e.point.y] as [number, number]];
        let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
        for (const [x, y] of pts) {
          if (x < x1) x1 = x;
          if (y < y1) y1 = y;
          if (x > x2) x2 = x;
          if (y > y2) y2 = y;
        }
        // A traced path that encloses real area is a lasso; a thin scribble
        // or straight swipe falls back to the old bbox-rectangle behavior.
        const bboxArea = (x2 - x1) * (y2 - y1);
        const usePolygon = pts.length >= 4 && bboxArea > 0 && lassoArea(pts) / bboxArea >= 0.2;
        if (Math.abs(x2 - x1) > 5 && Math.abs(y2 - y1) > 5) {
          try {
            const gridMode = useUIStore.getState().layoutGridSelectMode;
            const queryLayerIds = gridMode
              ? getAllVisibleGridFillLayerIds()
              : getAllVisibleLabelsLayerIds();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let features = map.queryRenderedFeatures([[x1, y1], [x2, y2]] as any, { layers: queryLayerIds });
            if (usePolygon) {
              features = features.filter((f) => {
                // labels are Points; grid cells are Polygons — hit-test the
                // cell's center so a lasso brushing a corner doesn't grab it
                const g = f.geometry as GeoJSON.Point | GeoJSON.Polygon | undefined;
                const coords = g?.type === 'Polygon'
                  ? polygonCenter(g.coordinates[0] as [number, number][])
                  : (g as GeoJSON.Point | undefined)?.coordinates;
                if (!coords) return false;
                const p = map.project(coords as [number, number]);
                return pointInScreenPolygon(p.x, p.y, pts);
              });
            }
            const { layoutLabelLevels: levels } = useMapStore.getState();
            // For rect-selection: group features by owning layout id.
            // The rule: activate the layout that owns the MOST selected
            // features, then take that layout's matching entity ids.
            const ownerCounts = new Map<string, number>();
            const featuresByOwner = new Map<string, string[]>();
            for (const f of features) {
              const entityId = f.properties?.entityId;
              const owner = f.properties?.layoutId;
              const level = f.properties?.level ?? 'unclassified';
              if (!entityId || !owner) continue;
              // grid cells carry no level property — the label-level toggles
              // must not silently filter a grid selection
              if (!gridMode && levels[level] === false) continue;
              ownerCounts.set(owner, (ownerCounts.get(owner) ?? 0) + 1);
              const arr = featuresByOwner.get(owner) ?? [];
              arr.push(entityId);
              featuresByOwner.set(owner, arr);
            }
            // Pick the most-represented owner
            let topOwner: string | null = null;
            let topCount = 0;
            for (const [k, v] of ownerCounts) {
              if (v > topCount) { topOwner = k; topCount = v; }
            }
            if (topOwner) {
              if (useProjectStore.getState().activeLayoutId !== topOwner) {
                useProjectStore.getState().setActiveLayoutId(topOwner);
              }
              const unique = [...new Set(featuresByOwner.get(topOwner) ?? [])];
              const isCtrl = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
              if (isCtrl) {
                const prev = useUIStore.getState().selectedLayoutEntityIds;
                const merged = [...new Set([...prev, ...unique])];
                useUIStore.getState().selectLayoutEntities(merged);
              } else {
                useUIStore.getState().selectLayoutEntities(unique);
              }
            }
          } catch { /* layer may not exist */ }
        }
        rectSel.svg.remove();
        rectSel.svg = null;
        rectSel.poly = null;
        rectSel.points = [];
      }
      rectSel.active = false;
    }

    // ── Label selection halo — applied to ALL layouts' labels layers ─────
    function updateLabelSelectionHalo() {
      if (!map) return;
      const selectedIds = useUIStore.getState().selectedLayoutEntityIds;
      for (const sanitized of handlersRef.current.keys()) {
        const layerId = layoutLabelsLayerId(sanitized);
        if (!map.getLayer(layerId)) continue;
        if (selectedIds.length > 0) {
          map.setPaintProperty(layerId, 'text-halo-color', [
            'case',
            ['in', ['get', 'entityId'], ['literal', selectedIds]],
            'rgba(19, 214, 198, 0.9)',
            'rgba(0,0,0,0.7)',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ] as any);
          map.setPaintProperty(layerId, 'text-halo-width', [
            'case',
            ['in', ['get', 'entityId'], ['literal', selectedIds]],
            2,
            1,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ] as any);
        } else {
          map.setPaintProperty(layerId, 'text-halo-color', 'rgba(0,0,0,0.7)');
          map.setPaintProperty(layerId, 'text-halo-width', 1);
        }
      }
    }

    const unsub4 = useUIStore.subscribe((state, prevState) => {
      if (state.selectedLayoutEntityIds !== prevState.selectedLayoutEntityIds) {
        updateLabelSelectionHalo();
        applyGridSelHighlight(); // self-clears when grid mode is off
      }
    });

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (moduleSel.modules.size > 0) clearModuleSelection();
        const selIds = useUIStore.getState().selectedLayoutEntityIds;
        if (selIds.length > 0) {
          useUIStore.getState().selectLayoutEntities([]);
          useUIStore.getState().selectLayoutEntity(null);
        }
        if (useUIStore.getState().layoutLabelSelectMode) {
          useUIStore.getState().setLayoutLabelSelectMode(false);
        }
        if (useUIStore.getState().layoutGridSelectMode) {
          useUIStore.getState().setLayoutGridSelectMode(false);
          clearGridSelHighlight();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);

    const unsub3 = useUIStore.subscribe((state, prevState) => {
      if (!map) return;
      if (state.layoutLabelAddMode !== prevState.layoutLabelAddMode) {
        map.getCanvas().style.cursor = state.layoutLabelAddMode ? 'crosshair' : '';
      }
      if (state.layoutLabelSelectMode !== prevState.layoutLabelSelectMode) {
        map.getCanvas().style.cursor = state.layoutLabelSelectMode ? 'crosshair' : '';
      }
      if (state.layoutGridSelectMode !== prevState.layoutGridSelectMode) {
        map.getCanvas().style.cursor = state.layoutGridSelectMode ? 'crosshair' : '';
        if (!state.layoutGridSelectMode) clearGridSelHighlight();
      }
    });

    // ── Per-layout handler registration ──────────────────────────────────
    //
    // The handler closures capture the OWNING layout id. On mousedown, we
    // first call setActiveLayoutId(ownerLayoutId) — synchronously swapping
    // the deprecated `data.siteLayout` alias to the clicked layout — and
    // then delegate to the inner handler, which reads the alias to find the
    // transform and entity. This is how click routing reaches the correct
    // layout's data without per-handler refactoring.

    function registerHandlersForLayout(targetMap: MapboxMap, rawLayoutId: string, sanitizedId: string) {
      const lLayerId = layoutLabelsLayerId(sanitizedId);
      const gFillLayerId = layoutGridFillLayerId(sanitizedId);

      const onLabelDown = (e: MapMouseEvent) => {
        // Finalize any stranded drag BEFORE the active-layout alias swap
        // below: the finalize commits through the ACTIVE layout's transform
        // and store update, so running it after the swap would write the old
        // drag's delta into the newly clicked layout — silently moving (or
        // fabricating overrides on) the wrong layout's entities.
        finalizeStaleDrags();
        if (useProjectStore.getState().activeLayoutId !== rawLayoutId) {
          useProjectStore.getState().setActiveLayoutId(rawLayoutId);
        }
        onLabelMouseDownInner(e, rawLayoutId, lLayerId);
      };
      const onLabelEnter = onLabelMouseEnterShared;
      const onLabelLeave = onLabelMouseLeaveShared;
      const onModuleDown = (e: MapMouseEvent) => {
        // labelCaptured = THIS SAME mousedown already armed a label drag
        // (labels register first). Finalizing here would kill that drag 1-2ms
        // after it was armed — the harness-proven "label overlapping a module
        // is undraggable" bug.
        if (!labelCaptured) finalizeStaleDrags(); // before the alias swap — see onLabelDown
        if (useProjectStore.getState().activeLayoutId !== rawLayoutId) {
          useProjectStore.getState().setActiveLayoutId(rawLayoutId);
        }
        onModuleMouseDownInner(e, rawLayoutId);
      };
      const onModuleEnter = onModuleMouseEnterShared;
      const onModuleLeave = onModuleMouseLeaveShared;

      // Non-interactive (share viewer): draw only. Registering nothing here
      // also keeps resumeHoverHandlers() a no-op, since it walks handlersRef.
      if (!interactive) return;

      targetMap.on('mousedown', lLayerId, onLabelDown);
      targetMap.on('mouseenter', lLayerId, onLabelEnter);
      targetMap.on('mouseleave', lLayerId, onLabelLeave);
      targetMap.on('mousedown', gFillLayerId, onModuleDown);
      targetMap.on('mouseenter', gFillLayerId, onModuleEnter);
      targetMap.on('mouseleave', gFillLayerId, onModuleLeave);

      handlersRef.current.set(sanitizedId, {
        rawLayoutId,
        labelsLayerId: lLayerId,
        gridFillLayerId: gFillLayerId,
        onLabelDown,
        onLabelEnter,
        onLabelLeave,
        onModuleDown,
        onModuleEnter,
        onModuleLeave,
      });
    }

    function unregisterHandlersForLayout(targetMap: MapboxMap, sanitizedId: string) {
      const h = handlersRef.current.get(sanitizedId);
      if (!h) return;
      try {
        targetMap.off('mousedown', h.labelsLayerId, h.onLabelDown);
        targetMap.off('mouseenter', h.labelsLayerId, h.onLabelEnter);
        targetMap.off('mouseleave', h.labelsLayerId, h.onLabelLeave);
        targetMap.off('mousedown', h.gridFillLayerId, h.onModuleDown);
        targetMap.off('mouseenter', h.gridFillLayerId, h.onModuleEnter);
        targetMap.off('mouseleave', h.gridFillLayerId, h.onModuleLeave);
      } catch { /* layer/handler may have been removed already */ }
      handlersRef.current.delete(sanitizedId);
    }

    if (map) {
      map.on('click', onAddLabelClick);
      map.on('mousedown', onMapMouseDownForRect);
    }

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
      window.removeEventListener('keydown', onKeyDown);
      if (rectSel.svg) { rectSel.svg.remove(); rectSel.svg = null; rectSel.poly = null; rectSel.points = []; }
      if (map) {
        // Tear down per-layout handlers + layers
        for (const sanitized of [...handlersRef.current.keys()]) {
          unregisterHandlersForLayout(map, sanitized);
        }
        for (const sanitized of [...prevLayoutsRef.current.keys()]) {
          removeLayerSetForLayout(map, sanitized);
        }
        prevLayoutsRef.current.clear();
        handlersRef.current.clear();
        map.off('mousemove', onDragMove);
        map.off('mousemove', onModuleDragMove);
        map.off('click', onAddLabelClick);
        map.off('mousedown', onMapMouseDownForRect);
        map.off('mousemove', onRectDragMove);
        // A teardown mid-drag must not leak the frozen state: without this
        // the ghost div, the disabled pan/zoom handlers, and the grabbing
        // cursor all outlive the effect when the map instance survives.
        if (drag.dragging || moduleDrag.dragging) {
          ghostDestroy();
          map.dragPan.enable();
          map.off('move', onDragCameraMove);
          map.getCanvas().style.cursor = '';
          drag.dragging = false;
          moduleDrag.dragging = false;
        }
        destroySettlingGhost();
      }
      window.removeEventListener('mouseup', onLabelWindowMouseUp, true);
      window.removeEventListener('mouseup', onModuleWindowMouseUp, true);
    };
  }, [isMapReady, mapRef, interactive]);
}
